import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Select,
  Row,
  Col,
  Button,
  Typography,
  Spin,
  Dropdown,
  Checkbox,
  Input,
  Modal,
  Carousel,
  Form,
  InputNumber,
  Space,
  Alert,
  Popconfirm,
  Collapse,
  AutoComplete,
  Switch,
  Upload,
  DatePicker,
  Segmented,
  Tag,
  Radio,
  Empty,
  Badge,
  Tooltip,
} from "antd";
import dayjs from "dayjs";
import { clpInputFormatter, clpInputParser } from "../../utils/clpInput";
import { formatPropertyName } from "../../utils/propertyDisplay";
import {
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  PlusOutlined,
  SettingOutlined,
  CameraOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  SearchOutlined,
  FilterOutlined,
  HistoryOutlined,
  InboxOutlined,
  MergeCellsOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import "antd/dist/reset.css";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";

import {
  useInventarioLogic,
  hayFiltros,
  SIN_CATEGORIA,
  Producto,
  FilaTabla,
  ModoAgrupacion,
  FiltrosInventario,
  COLUMNAS_ORDENABLES,
  COLUMNAS_ORDENABLES_GRUPO,
} from "./useInventarioLogic";
import "./inventario.css";
import { Inventory } from "../../interfaces/api.interfaces";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import { isAdminRole } from "../../utils/roles.utils";
import { compressImageFile, dataUrlToFile } from "../../utils/imageCompression";
import ImageEditorModal from "../../Components/ImageEditorModal";
import MoveProductModal from "./components/MoveProductModal";
import DeleteProductModal from "./components/DeleteProductModal";
import HistoriaProductoDrawer from "./components/HistoriaProductoDrawer";
import { ProductoMiniatura, UbicacionCell } from "./components/ProductoCells";
import CampoEtiqueta from "./components/CampoEtiqueta";
import UbicacionesProductoPopover from "./components/UbicacionesProductoPopover";
import UnificarProductosModal from "./components/UnificarProductosModal";
import RecomprarBuscador, {
  ProductoRecompra,
} from "./components/RecomprarBuscador";
import InventarioFinanzasTab from "./components/InventarioFinanzasTab";
import InventarioMovimientosTab from "./components/InventarioMovimientosTab";
// Bridge antd estático: `message.x` directo de "antd" no funciona en
// v5 + React 19 (root React huérfano fuera del ConfigProvider). Round
// 2026-05-01 — sweep general post-fix /sueldos.
import { getMsg, getModal } from "../../utils/antdStatic";
// Round 2026-06-01 — usados por la sección "Intermediación" del modal
// "Crear producto" (toggle "¿Alguien adelantó el dinero?").
import { apiFetch } from "../../services/apiClient";
import { inventarioApi } from "../../services/inventario.api";
import { userSelectOption } from "../../utils/userDisplay";
// Round 2026-07-26 — la categoría del producto usa LA MISMA lista de
// categorías que los flujos de /flujos (decisión del owner). Reusamos el
// espejo canónico del ENUM para no duplicar listas.
import { FLUJO_CATEGORIAS, CATEGORIA_LABEL } from "../flujos/flujos.api";
import { ahoraChile } from "../../utils/fechaChile";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import VideoPlayer from "../../Components/VideoPlayer";

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024; // 25 MB — límite cliente para imágenes/videos

const { Option } = Select;
const { TextArea } = Input;

/**
 * Qué va a pasar con un candidato del modal «¿es el mismo producto?».
 *
 * El texto sale de `accion`/`motivo`, que los calcula el BACKEND con el mismo
 * helper que después ejecuta la compra (services/destinoDeCompra.js). Acá vivía
 * una copia de la regla de compatibilidad de formatos y por eso el modal podía
 * prometer una fusión que el POST no hacía; ahora solo se traduce a español.
 */
const queHaraElCandidato = (c: any): string => {
  if (c?.accion === "fusion") return " · se suma al stock que ya está en el destino";
  if (c?.motivo === "nueva_por_formato") {
    return " · otro formato: queda como existencia aparte en el destino, mismo producto";
  }
  return " · se creará su existencia en el destino";
};

const REASONS = [
  { value: "falla", label: "Falla" },
  { value: "perdida", label: "Pérdida" },
  { value: "venta", label: "Venta" },
  { value: "robo", label: "Robo" },
  { value: "nunca_llego", label: "Nunca llegó" },
  { value: "otro", label: "Otro (especificar)" },
];

/**
 * Columnas visibles de fábrica. Se declara APARTE del estado para que
 * "Volver al default" tenga a qué volver: antes el default vivía inline en el
 * useState y no había forma de recuperarlo sin recargar la página.
 */
const COLUMNAS_POR_DEFECTO = [
  "id",
  "nombre",
  "categoria",
  "esPack",
  "unidadesPorPack",
  "cantidadDePacks",
  "cantidadDeUnidades",
  "ubicacion",
];

// Las columnas ordenables (planas y agrupadas) viven en `useInventarioLogic`,
// junto al estado `orden`, porque también son la lista contra la que se valida
// el orden restaurado de la sesión anterior.

/**
 * Columnas ancladas a la izquierda, con su ancho (AntD lo exige para fijar).
 *
 * Solo la IDENTIDAD: cuál es la fila y qué producto es. Anclar más achicaría el
 * área scrolleable justo en las pantallas donde el ancho ya escasea.
 */
const FIJAS_IZQUIERDA: Record<string, number> = {
  id: 84,
  nombre: 260,
};

/**
 * Sanea la elección de columnas guardada. Tolera basura (otra versión de la
 * app, edición manual del storage) devolviendo `undefined`, que para el hook
 * significa "descartá lo guardado y usá el default".
 * Filtra contra las columnas que EXISTEN hoy: una key vieja de una columna
 * eliminada se descarta sola.
 */
function sanearColumnas(crudo: unknown): string[] | undefined {
  if (!Array.isArray(crudo)) return undefined;
  const validas = allColumnsMeta.map((c) => c.key);
  const limpias = crudo.filter(
    (k): k is string => typeof k === "string" && validas.includes(k),
  );
  // Cero columnas visibles deja una tabla sin nada y sin forma obvia de
  // salir: se trata como preferencia inválida.
  return limpias.length > 0 ? limpias : undefined;
}

const allColumnsMeta = [
  {
    // Round 2026-08-06 — el id del producto no se mostraba en ninguna parte.
    // Sin él era imposible decir "la transacción #412 es de este producto" o
    // referirse a una fila concreta al pedir ayuda; y como los ids nunca se
    // reutilizan (las secuencias se re-alinean en cada arranque), el número es
    // un identificador estable e histórico, no un correlativo de pantalla.
    key: "id",
    title: "ID",
    dataIndex: "id",
    render: (id: number, r: FilaTabla) => {
      // Una cabecera de grupo no tiene código propio: agrupa varias. Mostrar
      // el `#0` de la fila sintética sería inventar un código que no existe.
      if (r.esGrupo) {
        return r.grupo?.tipo === "producto" && r.grupo.productoId ? (
          <Tooltip title="Código de la ficha del catálogo (el producto), no de una ubicación.">
            <Tag color="blue" className="inventario-id-tag">
              F{r.grupo.productoId}
            </Tag>
          </Tooltip>
        ) : null;
      }
      return (
        <Tooltip title="Código del producto. Estable e histórico: nunca se reasigna.">
          <Tag color="default" className="inventario-id-tag">
            #{id}
          </Tag>
        </Tooltip>
      );
    },
  },
  {
    key: "nombre",
    title: "Producto",
    dataIndex: "nombre",
    // Round 2026-08-06 — la celda pasó a ser la IDENTIDAD del producto:
    // miniatura + nombre + marca. Antes solo estaba el nombre, y decidir "¿es
    // el mismo producto?" con dos filas que se llaman igual era imposible.
    //
    // El nombre se recorta a 2 líneas con puntos suspensivos (CSS, no JS: así
    // el corte depende del ancho real de la columna) y el nombre completo
    // queda en el tooltip.
    render: (n: string, r: FilaTabla) => (
      <div className="inventario-producto-cell">
        <ProductoMiniatura producto={r} />
        <div className="inventario-producto-texto">
          <Tooltip title={n}>
            <span
              className={
                r.esGrupo
                  ? "inventario-producto-nombre inventario-grupo-nombre"
                  : "inventario-producto-nombre"
              }
            >
              {n}
            </span>
          </Tooltip>
          <div className="inventario-producto-meta">
            {r.marca ? (
              <span className="inventario-producto-marca">{r.marca}</span>
            ) : null}
            {/* En una cabecera el reparto ya está a la vista al desplegarla:
                el popover repetiría el mismo dato y, sobre todo, pediría
                `/inventario/:id/ubicaciones` con el id 0 de la fila sintética. */}
            {!r.esGrupo && (r.otrasUbicaciones ?? 0) > 0 && (
              <UbicacionesProductoPopover
                productoId={r.id}
                otras={r.otrasUbicaciones as number}
              />
            )}
            {r.esGrupo && r.grupo ? (
              <Tag color="blue" className="inventario-tag-mini">
                {r.grupo.tipo === "producto"
                  ? `en ${r.grupo.lugares} ${r.grupo.lugares === 1 ? "lugar" : "lugares"}`
                  : `${r.grupo.items} ${r.grupo.items === 1 ? "producto" : "productos"}`}
              </Tag>
            ) : null}
            {r.oculto && (
              <Tooltip title='Producto dado de baja. No aparece en el listado salvo que actives "Mostrar ocultos", y por eso tampoco se podía eliminar.'>
                <Tag color="default" className="inventario-tag-mini">
                  oculto
                </Tag>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    ),
  },
  {
    key: "categoria",
    title: "Categoría",
    dataIndex: "categoria",
    render: (categoria: string | null) =>
      categoria ? (
        <Tag color="geekblue">{CATEGORIA_LABEL[categoria] ?? categoria}</Tag>
      ) : (
        "—"
      ),
  },
  {
    key: "esPack",
    title: "Pack",
    dataIndex: "esPack",
    render: (esPack: boolean) => (esPack ? "Sí" : "No"),
  },
  {
    key: "unidadesPorPack",
    title: "Unidades por pack",
    dataIndex: "unidadesPorPack",
  },
  {
    key: "cantidadDePacks",
    title: "Cantidad de packs",
    dataIndex: "cantidadDePacks",
  },
  {
    key: "cantidadDeUnidades",
    title: "Total de unidades",
    dataIndex: "cantidadDeUnidades",
  },
  {
    key: "ubicacion",
    title: "Ubicación",
    dataIndex: "ubicacion",
    // Round 2026-08-06 — antes los tres tipos se veían igual (texto unido por
    // " · "): la única pista de que algo era una propiedad era que salía en
    // mayúsculas. Ahora cada tipo lleva su color y su ícono, que es lo que
    // permite leer la tabla de un vistazo.
    render: (_: unknown, r: FilaTabla) => {
      // Una cabecera de PRODUCTO no tiene una ubicación: tiene varias. Dibujar
      // un tag de lugar ahí afirmaría que el producto está en uno solo.
      if (r.esGrupo && r.grupo?.tipo === "producto") {
        return (
          <Typography.Text type="secondary">
            {r.grupo.lugares} {r.grupo.lugares === 1 ? "ubicación" : "ubicaciones"}
          </Typography.Text>
        );
      }
      return <UbicacionCell producto={r} />;
    },
  },
  { key: "marca", title: "Marca", dataIndex: "marca" },
  { key: "modelo", title: "Modelo", dataIndex: "modelo" },
  { key: "sku", title: "SKU", dataIndex: "sku" },
  { key: "material", title: "Material", dataIndex: "material" },
  { key: "color", title: "Color", dataIndex: "color" },
  { key: "forma", title: "Forma", dataIndex: "forma" },
  { key: "diseno", title: "Diseño", dataIndex: "diseno" },
  { key: "tamano", title: "Tamaño", dataIndex: "tamano" },
  { key: "volumenValor", title: "Volumen", dataIndex: "volumenValor" },
  { key: "pesoValor", title: "Peso", dataIndex: "pesoValor" },
  {
    key: "precioPorPack",
    title: "Precio pack",
    dataIndex: "precioPorPack",
  },
  {
    key: "precioPorUnidad",
    title: "Precio unidad",
    dataIndex: "precioPorUnidad",
  },
  { key: "numeroBoleta", title: "Boleta", dataIndex: "numeroBoleta" },
  { key: "numeroFactura", title: "Factura", dataIndex: "numeroFactura" },
];

export const Inventario: React.FC = () => {
  const {
    inventario,
    loading,
    error,
    page,
    size,
    total,
    moveParams,
    countries,
    regions,
    sectors,
    comunas,
    properties,
    bodegas,
    bodeguitas,
    filterName,
    filterBodegas,
    filterBodeguitas,
    filterProperties,
    filterCountries,
    filterRegions,
    filterComunas,
    filterSectors,
    filterCategorias,
    filterStock,
    advancedFilter,
    similarProducts,
    sidebarOpen,
    ubicacionesReady,
    toggleSidebar,
    aplicarFiltros,
    limpiarFiltros,
    setFilterComunas,
    setFilterName,
    setFilterCountries,
    setFilterRegions,
    setFilterSectors,
    setFilterBodegas,
    setFilterBodeguitas,
    setFilterProperties,
    setFilterCategorias,
    setFilterStock,
    setAdvancedFilter,
    setMoveParams,
    setSimilarProducts,
    setPage,
    cambiarSize,
    orden,
    cambiarOrden,
    agruparPor,
    cambiarAgrupacion,
    filasTabla,
    recargar,
    incluirOcultos,
    setIncluirOcultos,
    onUnhide,
    quitarFiltro,
    filtros,
    onCreate,
    onEdit,
    onDelete,
    onBulkDelete,
    user,
    onMove,
    uploadMediaFile,
    deleteImage,
    getSimilarProducts,
    handleHideProduct,
    suggestions,
  } = useInventarioLogic();

  // ¿El admin tiene algún filtro puesto? Decide el texto del empty state:
  // "no hay productos" y "ninguno coincide con el filtro" son problemas
  // distintos y la salida es distinta.
  const hayFiltrosPuestos = hayFiltros(filtros);

  // Lo que el botón "Limpiar filtros" tiene para hacer. Incluye "Mostrar
  // ocultos", que `hayFiltros` no mira (no viaja en `FiltrosInventario`) pero
  // `limpiarFiltros` sí apaga. No se mezcla con `hayFiltrosPuestos`: ese decide
  // el texto del empty state, y "Mostrar ocultos" AMPLÍA la lista — una tabla
  // vacía con el switch encendido significa que no hay productos, no que
  // ninguno coincida con un filtro.
  const hayQueLimpiar = hayFiltrosPuestos || incluirOcultos;

  // Modals
  const [isFormModalVisible, setIsFormModalVisible] = useState(false);
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [hideState, setHideState] = useState<{
    open: boolean;
    id: number | null;
    nombre?: string;
  }>({ open: false, id: null });
  const [editingProduct, setEditingProduct] = useState<Producto | null>(null);
  const [form] = Form.useForm();
  // `extraKeys` se eliminó: antes controlaba qué campos opcionales
  // mostrar en el modal (sistema "Agregar campos"). Round 2026-04-30
  // nonies — todos los campos opcionales se renderean siempre dentro de
  // un Collapse "Características", lo cual hace innecesario este estado.
  const [locationType, setLocationType] = useState<string>("");
  const [origenType, setOrigenType] = useState<string>("");
  const [selectedSimilarId, setSelectedSimilarId] = useState<number | null>(
    null,
  );
  const [currentProduct, setCurrentProduct] = useState<Producto | null>(null);
  const [images, setImages] = useState<string[]>([]);
  // url completa -> url de la versión liviana (480p) de cada video de la galería. El endpoint
  // trae los entries completos en `items`; `images` sigue siendo la lista plana de URLs.
  const [videoLivianas, setVideoLivianas] = useState<Record<string, string>>({});
  const [imagesLoading, setImagesLoading] = useState(false);
  const [lbOpen, setLbOpen] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  // Round 2026-07-26 — doble confirmación explícita de borrado (reemplaza el
  // Popconfirm). 1 producto = individual; N = masivo. El modal muestra el
  // impacto real (endpoint /impacto-eliminacion) antes de pedir confirmación.
  // `origen` distingue de dónde vino el borrado: "fila" (botón de la fila) o
  // "masivo" (barra de selección). NO se puede inferir de productos.length —
  // un borrado masivo de 1 producto igual tiene que limpiar la selección.
  const [deleteState, setDeleteState] = useState<{
    open: boolean;
    productos: Producto[];
    origen: "fila" | "masivo";
  }>({ open: false, productos: [], origen: "fila" });
  // Producto cuya línea de tiempo se está mirando (round 2026-08-06). El
  // nombre viaja aparte porque el drawer también sirve para productos ya
  // borrados, donde el backend no tiene de dónde sacarlo.
  const [historia, setHistoria] = useState<{
    id: number;
    nombre: string | null;
  } | null>(null);
  // Round 2026-07-26 — vista de la página: "productos" (tabla clásica) o
  // "finanzas" (toda la plata del inventario: transacciones, cobros,
  // reembolsos, resumen por categoría). Las finanzas de inventario viven
  // SOLO acá — se excluyeron de /flujos (decisión del owner).
  // Round 2026-08-06 — se suma "movimientos" (la bitácora): qué se compró y
  // qué se trasladó, con rango de fechas libre y filtro por lugar.
  const [vista, setVista] = useFiltroPersistido<
    "productos" | "movimientos" | "finanzas"
  >("inventario.vista", "productos", {
    validar: (v) => v === "productos" || v === "movimientos" || v === "finanzas",
  });
  // Round 2026-07-26 — prompt de FUSIÓN al crear una compra cuyo producto ya
  // existe en el destino (opción B del owner: promedio ponderado, opcional).
  const [fusionState, setFusionState] = useState<{
    open: boolean;
    payload: any | null;
    candidatos: any[];
    seleccionado: number | null;
  }>({ open: false, payload: null, candidatos: [], seleccionado: null });
  // Evita el doble submit del prompt de fusión: sin esto, dos clicks seguidos
  // crean/fusionan DOS veces la misma compra (stock y plata duplicados).
  const [fusionSubmitting, setFusionSubmitting] = useState(false);
  // La elección de columnas persiste entre sesiones (round 2026-08-06): con 22
  // columnas disponibles, re-armar la vista en cada visita era trabajo perdido.
  const [visibleKeys, setVisibleKeys] = useFiltroPersistido<string[]>(
    "inventario.columnas",
    COLUMNAS_POR_DEFECTO,
    { deserializar: sanearColumnas },
  );
  // Producto elegido en «¿Ya lo compraste antes?» (round 2026-08-06). Solo
  // sirve para mostrar de qué ficha se copió y su último precio conocido: la
  // compra que se crea es independiente, no queda ligada a esta fila.
  const [recompra, setRecompra] = useState<ProductoRecompra | null>(null);
  // Unificación de fichas (round 2026-08-06): «estos son el mismo producto».
  const [unificarAbierto, setUnificarAbierto] = useState(false);
  const [unificando, setUnificando] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);

  // Round 2026-04-30 nonies — distingue explícitamente al usuario si está
  // registrando una *compra nueva* (entrada de stock con boleta/factura
  // típicamente desde un proveedor externo) o un *movimiento* (transfer
  // de stock ya existente entre ubicaciones internas). Antes el modal
  // decía "Fecha de compra" en ambos casos, lo que confundía cuando el
  // usuario en realidad estaba moviendo un producto entre bodegas.
  // Cambia el label de la fecha y los defaults del bloque Origen.
  const [tipoOperacion, setTipoOperacion] = useState<"compra" | "movimiento">(
    "compra",
  );

  // ─── Sección "Intermediación" del modal Crear producto ────────────────
  // Round 2026-06-01 — toggle opcional: cuando admin marca que alguien
  // adelantó el dinero por AF (típicamente un aseador/trabajador que
  // compró en Líder con su tarjeta), generamos un flujo de reembolso
  // pendiente en `gastos`. La lógica financiera completa vive en
  // backend/src/services/inventoryFlowGenerator.js y está documentada
  // en docs/flujo-financiero.md §10.8. Si el toggle está off, AF se
  // asume como pagador directo (default actual).
  const [hayIntermediario, setHayIntermediario] = useState<boolean>(false);
  const [intermediarioRol, setIntermediarioRol] = useState<
    "aseador" | "trabajador" | "propietario" | "arrendatario" | "receptor_externo" | ""
  >("");
  const [intermediarioUsuarioId, setIntermediarioUsuarioId] = useState<number | undefined>(undefined);
  const [intermediarioRecipientId, setIntermediarioRecipientId] = useState<number | undefined>(undefined);
  // Round 2026-06-01 (iter b) — flag de "admin modificó algo de la
  // sección Intermediación en este modal de edición". Solo enviamos el
  // bloque al backend si fue tocado (evita regenerar flujos en cada
  // edit que solo cambia precio/cantidad). Reset al cerrar modal.
  const [intermediarioModificado, setIntermediarioModificado] = useState<boolean>(false);

  // ─── Estado de los flujos derivados (decisión #8, round 2026-06-21) ────
  // Hidratado desde GET /api/inventario/:id/intermediario al abrir el modal de
  // edición. Permite avisar si falta la RECUPERACIÓN (cobro al cliente) de un
  // producto entregado a una propiedad y ofrecer "Regenerar el cobro" — útil
  // cuando se borró por error desde /flujos. La transacción de inventario es la
  // fuente de verdad durable, así que el cobro se puede rehacer.
  const [tieneRecuperacion, setTieneRecuperacion] = useState<boolean>(false);
  const [tieneReembolso, setTieneReembolso] = useState<boolean>(false);
  const [regenerandoFlujos, setRegenerandoFlujos] = useState<boolean>(false);

  // ─── ¿El producto tiene una COMPRA registrada? (round 2026-08-05) ──────
  // La "Fecha de compra" NO vive en `inventario` sino en la transacción de
  // compra (`inventario_transacciones.fecha`), así que en modo edición hay que
  // traerla del backend. Sin esto el campo salía VACÍO al reabrir el producto y
  // lo que el admin escribiera se descartaba en silencio: parecía que la fecha
  // "no se guardaba". En `false` (producto nacido de un movimiento o data
  // legacy) no hay transacción de compra que corregir → campo deshabilitado.
  const [tieneCompraRegistrada, setTieneCompraRegistrada] = useState<boolean>(false);

  // Usuarios y receptores externos para los selects de la sección
  // Intermediación. Se cargan lazy solo cuando el modal de creación
  // se abre por primera vez (no en montaje del componente principal
  // para no costear el fetch a usuarios que solo vienen a editar
  // stock o ver listados).
  const [usuariosFinanzas, setUsuariosFinanzas] = useState<
    Array<{ id: number; rol: string; nombre: string; propiedades?: string[] }>
  >([]);
  const [expenseRecipients, setExpenseRecipients] = useState<
    Array<{ id: number; nombre: string; activo: boolean }>
  >([]);
  const [intermediacionCatalogosLoaded, setIntermediacionCatalogosLoaded] = useState(false);

  const cargarCatalogosIntermediacion = useCallback(async () => {
    if (intermediacionCatalogosLoaded) return;
    try {
      const [usersRes, recipientsRes] = await Promise.all([
        apiFetch<Array<{ id: number; rol: string; nombre: string; propiedades?: string[] }>>(
          "/users/finanzas",
        ),
        apiFetch<{ data: Array<{ id: number; nombre: string; activo: boolean }> }>(
          "/finanzas/empresa/recipients",
        ),
      ]);
      setUsuariosFinanzas(Array.isArray(usersRes) ? usersRes : []);
      const recArr = Array.isArray(recipientsRes?.data) ? recipientsRes.data : [];
      setExpenseRecipients(recArr.filter((r) => r.activo));
      setIntermediacionCatalogosLoaded(true);
    } catch {
      // Silencioso — si falla la carga, el admin ve los selects vacíos
      // y puede dejar el toggle off; el flujo principal de crear
      // producto no se interrumpe.
    }
  }, [intermediacionCatalogosLoaded]);

  const resetIntermediacion = useCallback(() => {
    setHayIntermediario(false);
    setIntermediarioRol("");
    setIntermediarioUsuarioId(undefined);
    setIntermediarioRecipientId(undefined);
    setIntermediarioModificado(false);
    setTieneRecuperacion(false);
    setTieneReembolso(false);
    setTieneCompraRegistrada(false);
  }, []);

  /**
   * Hidrata el estado de la sección Intermediación desde el backend
   * cuando admin abre el modal en modo edición.
   *
   * Round 2026-06-02 — antes solo hidrataba el `intermediario`. Ahora
   * el endpoint devuelve también `origenTipo`/`origenNombre` y un
   * flag `aplicaIntermediacion`. Razón: los datos del origen viven en
   * `inventario_transacciones`, no en `Inventory`, así que sin esta
   * hidratación el frontend NO sabía si el producto fue creado con
   * `origen='otros'`, y el gating `origenType === "otros"` quedaba
   * en false → la sección desaparecía en modo Edit.
   *
   * Ahora sincronizamos `origenType` state desde el backend para que
   * la sección aparezca correctamente.
   */
  const hidratarIntermediarioActual = useCallback(async (productId: number) => {
    try {
      const res = await apiFetch<{
        data: {
          aplicaIntermediacion: boolean;
          compraTxId: number | null;
          fecha: string | null;
          origenTipo: string | null;
          origenId: number | null;
          origenNombre: string | null;
          intermediario: null | {
            rol: string;
            usuario_id: number | null;
            recipient_id: number | null;
            nombre: string | null;
          };
          tieneRecuperacion?: boolean;
          tieneReembolso?: boolean;
        };
      }>(`/inventario/${productId}/intermediario`);
      const ctx = res?.data;
      if (!ctx) return;

      // Estado de los flujos derivados (decisión #8): si falta la recuperación
      // de un producto entregado a una propiedad, el modal ofrece regenerarla.
      setTieneRecuperacion(!!ctx.tieneRecuperacion);
      setTieneReembolso(!!ctx.tieneReembolso);

      // Round 2026-08-05 — hidratar la FECHA DE COMPRA real. Vive en la
      // transacción, no en el Inventory: sin esto el campo aparecía vacío al
      // editar y el admin creía que nunca se había guardado.
      setTieneCompraRegistrada(!!ctx.compraTxId);
      form.setFieldsValue({ fecha: ctx.fecha ? dayjs(ctx.fecha) : undefined });

      // Sincronizar el state `origenType` con lo guardado en
      // inventario_transacciones. Esto es CRÍTICO para que el gating
      // de la sección funcione en Edit (`origenType === "otros"`).
      if (ctx.origenTipo) {
        setOrigenType(ctx.origenTipo);
        form.setFieldsValue({
          origenTipo: ctx.origenTipo,
          origenNombre: ctx.origenNombre ?? undefined,
          // El origen interno (bodega/bodeguita/propiedad) también vive en la
          // transacción: sin hidratarlo, el Select quedaba en blanco.
          origenId: ctx.origenId ?? undefined,
        });
      }

      const actual = ctx.intermediario;
      if (actual && actual.rol) {
        setHayIntermediario(true);
        setIntermediarioRol(actual.rol as any);
        setIntermediarioUsuarioId(actual.usuario_id ?? undefined);
        setIntermediarioRecipientId(actual.recipient_id ?? undefined);
      } else {
        setHayIntermediario(false);
        setIntermediarioRol("");
        setIntermediarioUsuarioId(undefined);
        setIntermediarioRecipientId(undefined);
      }
      // Importante: el flag de "modificado" arranca en false. Solo se
      // activa cuando admin cambia algo desde la UI.
      setIntermediarioModificado(false);
    } catch {
      // Silencioso — si falla el fetch, el admin ve la sección en
      // estado off; puede activarla manualmente si quiere registrar
      // el reembolso ahora.
    }
  }, [form]);

  /**
   * Regenera (upsert idempotente, no destructivo) los flujos derivados
   * FALTANTES del producto en edición. Hoy recrea la RECUPERACIÓN (cobro al
   * cliente) borrada por error desde /flujos, desde la transacción de compra.
   * Tras regenerar, re-hidrata el estado para que el aviso desaparezca.
   * Decisión #8 del modelo de inventario.
   */
  const handleRegenerarFlujos = useCallback(async () => {
    if (!editingProduct?.id) return;
    setRegenerandoFlujos(true);
    try {
      const res = await inventarioApi.regenerarFlujos(editingProduct.id);
      if (res?.success) {
        getMsg()?.success(res.message || "Flujos de inventario regenerados");
        await hidratarIntermediarioActual(editingProduct.id);
      }
    } finally {
      setRegenerandoFlujos(false);
    }
  }, [editingProduct, hidratarIntermediarioActual]);

  // Selección masiva para borrado en lote — solo admin/superadmin.
  // `selectedRowKeys` guarda los `id` numéricos (rowKey="id"). Se limpia
  // tras un bulkDelete exitoso o al cambiar de página/filtro grueso (no
  // forzamos esto último para no frustrar al usuario si quiere mantener
  // selección entre filtros — AntD ya muestra contador y permite "limpiar").
  const isAdmin = isAdminRole(user?.rol);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  /**
   * La selección REAL: solo existencias.
   *
   * Agrupado, marcar una cabecera hace que AntD meta también su clave
   * (`"g:p:101"`) en `selectedRowKeys`, porque necesita esa clave para dibujar
   * el checkbox marcado. Pero una cabecera no es un registro: no se puede
   * borrar ni unificar. Todo lo que cuenta o actúa usa esta lista.
   *
   * Sin ella, "3 productos seleccionados" mostraría 4 al marcar un grupo de 3.
   */
  const idsSeleccionados = useMemo(
    () => selectedRowKeys.map((k) => Number(k)).filter((n) => Number.isFinite(n)),
    [selectedRowKeys],
  );

  // `idsArg` permite reintentar desde el modal de confirmación aunque la
  // selección ya se haya limpiado: si dependiera solo de `selectedRowKeys`, un
  // segundo intento salía por el early-return sin borrar, sin avisar y sin
  // cerrar el modal (botón inerte).
  const handleBulkDelete = async (idsArg?: number[]): Promise<boolean> => {
    const ids = (idsArg ?? selectedRowKeys.map((k) => Number(k))).filter((n) =>
      Number.isFinite(n),
    );
    if (!ids.length) return false;
    setBulkDeleting(true);
    try {
      const { ok, fail } = await onBulkDelete(ids);
      if (ok > 0 && fail === 0) {
        getMsg()?.success(`${ok} producto${ok === 1 ? "" : "s"} eliminado${ok === 1 ? "" : "s"}`);
      } else if (ok > 0 && fail > 0) {
        getMsg()?.warning(`${ok} eliminado${ok === 1 ? "" : "s"}, ${fail} con error`);
      } else {
        getMsg()?.error("No se pudo eliminar ningún producto");
      }
      // Solo se limpia si algo se borró: si fallaron todos, la barra masiva
      // conserva la selección para poder reintentar también desde afuera.
      if (ok > 0) setSelectedRowKeys([]);
      return ok > 0;
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error en borrado masivo");
      return false;
    } finally {
      setBulkDeleting(false);
    }
  };

  // Round 2026-07-26 — ejecuta el borrado tras la doble confirmación del
  // modal de impacto. Individual usa onDelete; masivo delega en
  // handleBulkDelete (que ya agrega el toast y limpia la selección).
  const confirmarEliminacion = async () => {
    const prods = deleteState.productos;
    if (!prods.length) {
      setDeleteState({ open: false, productos: [], origen: "fila" });
      return;
    }
    // Se enruta por ORIGEN, no por cantidad: borrar 1 producto desde la barra
    // masiva tiene que pasar igual por handleBulkDelete, que es el único que
    // limpia `selectedRowKeys` (con preserveSelectedRowKeys, si no la barra
    // queda apuntando a un id ya inexistente).
    if (deleteState.origen === "masivo") {
      const ok = await handleBulkDelete(prods.map((p) => p.id));
      if (ok) setDeleteState({ open: false, productos: [], origen: "fila" });
      return;
    }
    setDeletingId(prods[0].id);
    try {
      // `onDelete` NUNCA lanza (apiFetch se traga el error y devuelve null),
      // así que el éxito se decide por su valor de retorno. El toast de éxito
      // lo emite apiFetch (`successMsg` de inventarioApi.delete) — acá solo
      // dejamos el modal abierto si falló, para no simular un borrado que no
      // ocurrió.
      const ok = await onDelete(prods[0]);
      if (ok) setDeleteState({ open: false, productos: [], origen: "fila" });
    } finally {
      setDeletingId(null);
    }
  };

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aplicarFiltrosRef = useRef(aplicarFiltros);
  aplicarFiltrosRef.current = aplicarFiltros;

  /**
   * Agenda una aplicación de filtros con rebote.
   *
   * Round 2026-08-05 — antes solo el buscador por nombre aplicaba solo; los
   * siete selects exigían apretar "Aplicar filtros". Eso dejaba dos modelos
   * mentales conviviendo y, peor, un estado intermedio mentiroso: los chips y
   * el contador "Filtros activos (N)" mostraban lo TIPEADO, no lo APLICADO, y
   * escribir una letra en el buscador aplicaba de golpe todo lo que estuviera
   * pendiente en los selects. Ahora TODO filtro aplica solo, así que lo que
   * ves en los chips es siempre lo que está filtrando la tabla.
   */
  const programarAplicar = useCallback((ms = 350) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void aplicarFiltrosRef.current();
    }, ms);
  }, []);

  const handleNameSearchChange = useCallback(
    (value: string | undefined) => {
      setFilterName(value);
      programarAplicar();
    },
    [setFilterName, programarAplicar],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const toggleColumn = (key: string, checked: boolean) => {
    setVisibleKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
  };

  // La elección de columnas la persiste `useFiltroPersistido` (por usuario):
  // las tres vías de cambio —casilla suelta, "mostrar todas", "volver al
  // default"— pasan por `setVisibleKeys`, así que no hay nada que repetir.

  // Round 2026-08-06 — el menú arranca con dos atajos. Con 22 casillas,
  // "mostrar todo" y "volver al default" a mano eran 20 clicks.
  const columnMenu = [
    {
      key: "__todas",
      label: (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setVisibleKeys(allColumnsMeta.map((c) => c.key))}
        >
          Mostrar todas ({allColumnsMeta.length})
        </Button>
      ),
    },
    {
      key: "__default",
      label: (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => setVisibleKeys(COLUMNAS_POR_DEFECTO)}
        >
          Volver a las de siempre
        </Button>
      ),
    },
    { key: "__sep", type: "divider" as const },
    ...allColumnsMeta.map((col) => ({
      key: col.key,
      label: (
        <Checkbox
          checked={visibleKeys.includes(col.key)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleColumn(col.key, !visibleKeys.includes(col.key))}
        >
          {col.title}
        </Checkbox>
      ),
    })),
  ];

  const columns = allColumnsMeta
    .filter((col) => visibleKeys.includes(col.key))
    .map(
      (col) =>
        ({
          title: col.title,
          dataIndex: col.dataIndex,
          key: col.key,
          render: col.render,
          // La IDENTIDAD viaja fija a la izquierda. Con la tabla más ancha que
          // la pantalla, scrolear para ver una columna del final dejaba fuera
          // el nombre: se leía un número sin saber de qué producto era. Ahora
          // la fila siempre dice qué es, mires la columna que mires.
          ...(FIJAS_IZQUIERDA[col.key]
            ? { fixed: "left" as const, width: FIJAS_IZQUIERDA[col.key] }
            : {}),
          // `sorter: true` = "esta columna se ordena, pero lo hace el
          // servidor". Sin `sortOrder` explícito la flecha se perdería al
          // recargar los datos, porque la tabla se remonta con cada respuesta.
          //
          // Agrupado se ordenan los GRUPOS, y por lo que la cabecera MUESTRA:
          // en las columnas de cantidad eso es la SUMA del grupo, no el valor
          // de una fila. Por eso la lista de columnas ordenables es distinta
          // (`COLUMNAS_ORDENABLES_GRUPO`). Las que quedan fuera pierden el
          // sorter a propósito: ofrecer un orden que el servidor no aplica
          // saldría ordenado por otra cosa con la flecha apuntando donde el
          // usuario pidió — el error se vería como un acierto.
          ...((agruparPor ? COLUMNAS_ORDENABLES_GRUPO : COLUMNAS_ORDENABLES).has(col.key)
            ? {
                sorter: true,
                sortOrder: orden.columna === col.key ? orden.direccion : null,
              }
            : {}),
        }) as any,
    );
  columns.push({
    title: "Acciones",
    key: "acciones",
    // ── Por qué esta columna está FIJA y por qué las acciones se agruparon ──
    //
    // El problema real: con 22 columnas posibles la tabla es más ancha que la
    // pantalla, y Acciones —al final— se salía por la derecha. Para editar una
    // fila había que scrolear horizontalmente, y ahí ya no se ve de QUÉ fila se
    // trata: el nombre quedó del otro lado.
    //
    // Se evaluó meter las acciones en una segunda subfila dentro de cada fila.
    // Se descartó: duplica la altura de la tabla (25 filas pasan de ~1.350px a
    // ~2.000px), o sea cambia scroll horizontal por MÁS scroll vertical, y deja
    // los botones lejos de los datos igual si estás scroleado.
    //
    // La columna fija a la derecha resuelve el problema entero: las acciones
    // quedan siempre a la vista, sin importar cuántas columnas se muestren ni
    // dónde esté el scroll. Y agrupar las secundarias en un menú ⋮ baja el
    // ancho de ~250px a ~96px, que es espacio que vuelve a los datos.
    fixed: "right",
    width: 96,
    render: (_: any, record: FilaTabla) =>
      // Una cabecera de grupo NO ofrece acciones, y es deliberado. Todas operan
      // sobre una existencia concreta: "mover" necesita un origen, "editar"
      // abre una fila, "eliminar" borraría el producto de TODAS sus ubicaciones
      // de un clic. Ofrecerlas sobre el grupo sería ambiguo justo donde el
      // costo de equivocarse es perder stock e historial de compras.
      record.esGrupo ? null : (
        <div className="acciones-cell">
          {/* Las dos de todos los días quedan a un clic. El resto vive en el
              menú: no se usan seguido y cada botón extra es ancho que le falta
              a las columnas de datos. */}
          <Tooltip title="Editar este producto">
            <Button
              icon={<EditOutlined />}
              onClick={() => openFormModal("edit", record)}
            />
          </Tooltip>
          <Tooltip title="Mover stock a otra ubicación">
            <Button icon={<SendOutlined />} onClick={() => openMoveModal(record)} />
          </Tooltip>
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "fotos",
                  icon: <CameraOutlined />,
                  label: "Fotos",
                  onClick: () => openImageModal(record),
                },
                {
                  // Línea de tiempo del producto: de dónde salió esta cantidad,
                  // a qué lugares se fue, qué plata movió cada paso.
                  key: "historia",
                  icon: <HistoryOutlined />,
                  label: "Ver su historia",
                  onClick: () => setHistoria({ id: record.id, nombre: record.nombre }),
                },
                { type: "divider" as const },
                // Ocultar era un camino de ida: el producto salía del listado y
                // con él este mismo menú, así que no había forma de revertirlo
                // ni de eliminarlo después. Con "Mostrar ocultos" activo se
                // puede devolver a la lista.
                record.oculto
                  ? {
                      key: "mostrar",
                      icon: <EyeOutlined />,
                      label: "Volver a mostrarlo",
                      onClick: () => void onUnhide(record.id),
                    }
                  : {
                      key: "ocultar",
                      icon: <EyeInvisibleOutlined />,
                      label: "Ocultar (dar de baja)",
                      onClick: () => openHide(record.id, record.nombre),
                    },
                {
                  // El borrado no es un Popconfirm de un clic: abre el modal de
                  // doble confirmación con el impacto explícito (2026-07-26).
                  key: "eliminar",
                  icon: <DeleteOutlined />,
                  label: "Eliminar",
                  danger: true,
                  disabled: deletingId !== null && deletingId !== record.id,
                  onClick: () =>
                    setDeleteState({ open: true, productos: [record], origen: "fila" }),
                },
              ],
            }}
          >
            <Tooltip title="Más acciones">
              <Button
                icon={<MoreOutlined />}
                loading={deletingId === record.id}
              />
            </Tooltip>
          </Dropdown>
        </div>
      ),
  });

  // Round 2026-08-06 — acá había un override que pisaba el render de la columna
  // Ubicación con un `r.ubicacion || "—"` plano, anulando lo que declarara
  // `allColumnsMeta`. Se eliminó: el render vive en un solo lugar
  // (`UbicacionCell`), que además distingue bodega / bodeguita / propiedad.
  // Dos definiciones para la misma celda es exactamente cómo se pierde un
  // cambio sin que nadie note por qué.

  const openHide = (id: number, nombre?: string) =>
    setHideState({ open: true, id, nombre });
  const closeHide = () =>
    setHideState({ open: false, id: null, nombre: undefined });

  /**
   * Copia la ficha de un producto que ya existe al formulario de compra.
   *
   * Copia QUÉ ES la cosa. No toca cantidades, precios, destino ni boleta: eso
   * es lo que esta compra trae de nuevo.
   *
   * El precio queda deliberadamente afuera aunque se conozca. Prellenarlo sería
   * cómodo el 90 % de las veces y desastroso el otro 10: es el dato que cambia
   * de compra a compra y el único que corrompe las finanzas si entra mal sin que
   * nadie lo mire — y un número ya escrito en la casilla no se revisa. Se
   * MUESTRA como referencia en el banner, que es donde sí se lee.
   */
  const aplicarRecompra = (p: ProductoRecompra) => {
    setRecompra(p);
    form.setFieldsValue({
      nombre: p.nombre,
      categoria: p.categoria ?? undefined,
      marca: p.marca ?? undefined,
      modelo: p.modelo ?? undefined,
      sku: p.sku ?? undefined,
      material: p.material ?? undefined,
      color: p.color ?? undefined,
      forma: p.forma ?? undefined,
      diseno: p.diseno ?? undefined,
      tamano: p.tamano ?? undefined,
      volumenValor: p.volumenValor ?? undefined,
      volumenUnidad: p.volumenUnidad ?? undefined,
      pesoValor: p.pesoValor ?? undefined,
      pesoUnidad: p.pesoUnidad ?? undefined,
      esPack: p.esPack,
      unidadesPorPack: p.unidadesPorPack || undefined,
    });
  };

  /** Vuelve al formulario en blanco conservando lo que no es de la ficha
   *  (fecha, destino ya elegido): borrar todo castigaría al que se equivocó de
   *  producto pero ya había avanzado. */
  const limpiarRecompra = () => {
    setRecompra(null);
    form.setFieldsValue({
      nombre: undefined,
      categoria: undefined,
      marca: undefined,
      modelo: undefined,
      sku: undefined,
      material: undefined,
      color: undefined,
      forma: undefined,
      diseno: undefined,
      tamano: undefined,
      volumenValor: undefined,
      volumenUnidad: undefined,
      pesoValor: undefined,
      pesoUnidad: undefined,
    });
  };

  /**
   * Confirma que las filas seleccionadas son un solo producto.
   *
   * Se limpia la selección al terminar: después de unificar, seguir con las
   * mismas filas marcadas invita a repetir la acción sobre algo que ya está
   * unificado.
   */
  const confirmarUnificacion = async (principalId: number) => {
    const ids = selectedRowKeys.map((k) => Number(k)).filter((n) => Number.isFinite(n));
    setUnificando(true);
    const res = await inventarioApi.unificar(ids, principalId);
    setUnificando(false);
    if (!res?.success) return;
    getMsg()?.success(res.message ?? "Productos unificados");
    setUnificarAbierto(false);
    setSelectedRowKeys([]);
    recargar();
  };

  const openFormModal = (mode: "create" | "edit", product?: Producto) => {
    setEditingProduct(mode === "edit" && product ? product : null);
    form.resetFields();
    setRecompra(null);
    if (mode === "edit" && product) {
      const vals: any = { ...product };
      if (vals.esPack && vals.precioPorPack && vals.cantidadDePacks > 0) {
        vals.precioTotal = vals.precioPorPack * vals.cantidadDePacks;
      } else if (vals.precioPorUnidad && vals.cantidadDeUnidades > 0) {
        vals.precioTotal = vals.precioPorUnidad * vals.cantidadDeUnidades;
      }
      form.setFieldsValue(vals);
      setLocationType(vals.ubicacionTipo);
    } else {
      setLocationType("");
      setOrigenType("");
      form.setFieldsValue({ fecha: ahoraChile() });
    }
    setPendingFiles([]);
    // Round 2026-06-01 — limpiar y precargar catálogos de la sección
    // "Intermediación". Lazy load para no costear el fetch en cada
    // render del listado de inventario.
    resetIntermediacion();
    void cargarCatalogosIntermediacion();
    // Round 2026-06-02 — el Segmented que elige entre "compra" /
    // "movimiento" solo se renderiza en modo create. En modo edit
    // forzamos `tipoOperacion="compra"` para que la sección
    // Intermediación pueda evaluarse correctamente. Si el producto
    // fue creado como movimiento (no compra), la hidratación del
    // origen vía hidratarIntermediarioActual marcará origenType en
    // algo distinto a "otros" y la sección quedará oculta por el
    // gating de `origenType === "otros"`.
    if (mode === "edit") {
      setTipoOperacion("compra");
    }
    // Round 2026-06-01 (iter b) — si abrimos el modal en modo edición,
    // hidratamos el intermediario actual del producto desde backend.
    // Esto permite que el admin vea/modifique el reembolso ya existente
    // sin tener que borrar y recrear el producto.
    if (mode === "edit" && product?.id) {
      void hidratarIntermediarioActual(product.id);
    }
    setIsFormModalVisible(true);
  };

  // Abre el modal limpiando `images` y activando `imagesLoading` antes del
  // fetch para que el render muestre spinner en vez del placeholder vacío
  // "No hay imágenes" mientras el backend responde.
  const openImageModal = async (product: Producto) => {
    setCurrentProduct(product);
    setImages([]);
    setVideoLivianas({});
    setImagesLoading(true);
    setIsImageModalVisible(true); // muestra el modal de inmediato
    try {
      const r = await inventarioApi.getImages(product.id);
      setImages(Array.isArray(r?.data) ? (r.data as string[]) : []);
      const livianas: Record<string, string> = {};
      for (const it of Array.isArray(r?.items) ? (r.items as any[]) : []) {
        if (it && typeof it === "object" && it.url && it.previewUrl) {
          livianas[String(it.url)] = String(it.previewUrl);
        }
      }
      setVideoLivianas(livianas);
    } catch {
      getMsg()?.error("Error cargando imágenes");
      setImages([]);
    } finally {
      setImagesLoading(false);
    }
  };

  // Recibe el dataUrl YA comprimido por ImageEditorModal. Convierte a
  // File y sube como cualquier otro. No vuelve a comprimir.
  const handleEditorSave = async (compressedDataUrl: string) => {
    if (!currentProduct) return;
    try {
      setSaving(true);
      const file = dataUrlToFile(compressedDataUrl, `producto${currentProduct.id}-edit-${Date.now()}`);
      const newUrl = await uploadMediaFile(currentProduct.id, file);
      setImages((prev) => [...prev, newUrl]);
      getMsg()?.success("Imagen agregada");
      setIsEditorVisible(false);
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error guardando imagen");
    } finally {
      setSaving(false);
    }
  };

  // Sube un File (imagen o video) al backend. Si es imagen, primero la
  // comprime con `compressImageFile` aplicando los parámetros de la config
  // admin. Videos van tal cual. Límite cliente (25 MB) sobre el original.
  const uploadDirect = async (file: File) => {
    if (!currentProduct || saving) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      getMsg()?.error(
        `Archivo demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo permitido: 25 MB.`,
      );
      return;
    }
    try {
      setSaving(true);
      const toUpload = await compressImageFile(file);
      const newUrl = await uploadMediaFile(currentProduct.id, toUpload);
      setImages((prev) => [...prev, newUrl]);
      getMsg()?.success(file.type.startsWith("video/") ? "Video agregado" : "Imagen agregada");
    } catch (e: any) {
      getMsg()?.error(e?.message || "Error subiendo archivo");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async (url: string) => {
    if (!currentProduct) return;
    const prev = images;
    setImages(prev.filter((u) => u !== url)); // optimista
    try {
      const updatedUrls = await deleteImage(currentProduct.id, url);
      setImages(updatedUrls); // reconciliar con backend
    } catch (e: any) {
      setImages(prev);
      getMsg()?.error(e?.message || "No se pudo eliminar la imagen");
      throw e;
    }
  };

  const openMoveModal = (product: Producto) => {
    setMoveParams({
      productId: product.id,
      origen_tipo: product.ubicacionTipo,
      origen_id: product.ubicacionId,
      destino_tipo: "",
      destino_id: undefined,
      cantidad_de_unidades: 0,
      cantidad_de_packs: product.esPack ? 1 : 0,
      es_pack: product.esPack,
      cargo_a: "",
      fecha: ahoraChile().format("YYYY-MM-DD"),
    });
    setSimilarProducts([]);
    setIsMoveModalVisible(true);
  };

  // Efecto para buscar similares cuando destino cambia
  useEffect(() => {
    const { destino_tipo, destino_id } = moveParams;
    if (!destino_tipo || !destino_id) {
      setSimilarProducts([]);
      setSelectedSimilarId(null);
      return;
    }
    getSimilarProducts(moveParams) // ← ahora devuelve array
      .then((list: Inventory[]) => {
        setSimilarProducts(list || []);
        // preselecciona el primero, si quieres:
        if (list?.length) {
          setSelectedSimilarId(list[0].id);
          setMoveParams((p) => ({ ...p, destinoProductId: list[0].id }));
        } else {
          setSelectedSimilarId(null);
          setMoveParams((p) => {
            const { destinoProductId, ...rest } = p as any;
            return rest; // quita destinoProductId si no hay sugerencias
          });
        }
      })
      .catch(() => {
        getMsg()?.error("Error al buscar productos similares");
        setSimilarProducts([]);
        setSelectedSimilarId(null);
      });
  }, [moveParams.destino_tipo, moveParams.destino_id]);

  // Ctrl+V paste support for images — active when the image modal is open
  useEffect(() => {
    if (!isImageModalVisible) return;
    const handler = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) =>
        f.type.startsWith("image/"),
      );
      if (!file) return;
      e.preventDefault();
      uploadDirect(file);
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageModalVisible, currentProduct, saving]);

  // Ctrl+V paste support for the create form
  useEffect(() => {
    if (!isFormModalVisible || editingProduct) return;
    const handler = (e: ClipboardEvent) => {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) =>
        f.type.startsWith("image/"),
      );
      if (!file) return;
      e.preventDefault();
      setPendingFiles((prev) => [...prev, file]);
      getMsg()?.success("Imagen pegada desde el portapapeles");
    };
    document.addEventListener("paste", handler);
    return () => document.removeEventListener("paste", handler);
  }, [isFormModalVisible, editingProduct]);

  const handleConfirmMove = async () => {
    if (!moveParams.cargo_a) {
      getMsg()?.warning("Selecciona a quién se carga el movimiento");
      return;
    }
    try {
      await onMove(moveParams);
      getMsg()?.success("Movimiento exitoso");
    } catch {
      getMsg()?.error("Error realizando movimiento");
    }
    setIsMoveModalVisible(false);
  };

  const onFinishForm = async (values: any) => {
    const { precioTotal, fecha, ...payload } = values;
    if (fecha) {
      payload.fecha = fecha.format("YYYY-MM-DD");
    }
    // Round 2026-06-01 — bloque `intermediario` en el payload.
    //
    // En CREACIÓN: si admin marcó el toggle y completó los campos, se
    // adjunta el bloque y backend genera 1-2 rows en `gastos`.
    //
    // En EDICIÓN (iter b): solo se adjunta el bloque si admin tocó algo
    // de la sección Intermediación (`intermediarioModificado=true`).
    // El backend usa `intermediarioModificado` como flag para decidir
    // si regenera los flujos derivados (delete + create) o los deja
    // intactos. Esto evita borrar y recrear los rows cada vez que admin
    // solo ajusta un precio.
    //
    // Validación previa: rol elegido + usuario/recipient según corresponda.
    // Si algún campo crítico falta y admin marcó el toggle, mostramos
    // un warning pero NO bloqueamos el submit del producto — admin
    // puede arreglar el flujo manualmente desde /flujos después.
    const seccionAplica =
      tipoOperacion === "compra" && origenType === "otros" && hayIntermediario && intermediarioRol;
    if (seccionAplica) {
      const valido =
        intermediarioRol === "receptor_externo"
          ? !!intermediarioRecipientId
          : intermediarioRol === "arrendatario"
            ? true
            : !!intermediarioUsuarioId;
      if (valido) {
        const nombreSnapshot = (() => {
          if (intermediarioRol === "receptor_externo") {
            return expenseRecipients.find((r) => r.id === intermediarioRecipientId)?.nombre || null;
          }
          if (intermediarioRol === "arrendatario") return "Arrendatario";
          return usuariosFinanzas.find((u) => u.id === intermediarioUsuarioId)?.nombre || null;
        })();
        payload.intermediario = {
          rol: intermediarioRol,
          usuario_id: intermediarioUsuarioId ?? null,
          recipient_id: intermediarioRecipientId ?? null,
          nombre: nombreSnapshot,
        };
      } else {
        getMsg()?.warning(
          "Falta seleccionar el usuario/receptor del intermediario. El producto se guardará sin reembolso vinculado.",
        );
      }
    }
    // En edición, además marcamos el flag para que backend sepa que
    // debe regenerar (delete + create) los flujos derivados.
    if (editingProduct && intermediarioModificado) {
      payload.intermediarioModificado = true;
      // Si admin desactivó el toggle, indicamos explícitamente null
      // para que backend borre los flujos derivados.
      if (!hayIntermediario) {
        payload.intermediario = null;
      }
    }
    if (editingProduct) {
      onEdit({ ...editingProduct, ...payload });
      setPendingFiles([]);
      setIsFormModalVisible(false);
      form.resetFields();
      resetIntermediacion();
      return;
    }

    // Round 2026-07-26 — antes de crear, buscar productos con nombre parecido
    // y preguntarle al admin si alguno es EL MISMO. Si lo es, la compra se
    // engancha a su ficha; si no, nace un producto aparte (p. ej. otra marca).
    // El modal del formulario queda abierto detrás.
    //
    // El formato de ESTA compra viaja en el request (round 2026-08-20): es lo
    // que le permite al backend decir, por candidato, si el stock se va a sumar
    // a una fila del destino o si va a nacer una existencia nueva ahí.
    if (payload.nombre && payload.ubicacionTipo && payload.ubicacionId != null) {
      const simResp = await inventarioApi.getSimilaresCompra({
        nombre: payload.nombre,
        ubicacionTipo: payload.ubicacionTipo,
        ubicacionId: Number(payload.ubicacionId),
        esPack: !!payload.esPack,
        unidadesPorPack: Number(payload.unidadesPorPack) || 0,
        // Si el admin ya señaló cuál es, que venga sí o sí en la lista: el
        // vínculo a la ficha sale de acá y depender de que el fuzzy lo
        // reencuentre es cómo se colaba un duplicado justo después de que la
        // persona eligiera el producto correcto.
        ...(recompra ? { incluirId: recompra.id } : {}),
      });

      // `apiFetch` NUNCA lanza: ante un 403, un 500 o la red caída devuelve
      // null. Eso se confundía con "no hay similares" y la creación seguía de
      // largo en silencio, dejando un producto DUPLICADO en la ubicación en
      // lugar de ofrecer la fusión. Ahora se distingue el fallo del resultado
      // vacío y decide el admin. Round 2026-08-05.
      if (simResp === null) {
        const seguir = await new Promise<boolean>((resolve) => {
          const m = getModal();
          if (!m) {
            resolve(false);
            return;
          }
          m.confirm({
            title: "No se pudo verificar si el producto ya existe ahí",
            content:
              "Falló la búsqueda de productos similares en la ubicación de destino, así que no podemos ofrecerte fusionar. Si el producto ya estaba ahí, vas a quedar con dos filas separadas.",
            okText: "Crear de todas formas",
            cancelText: "Cancelar",
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (!seguir) return;
      }

      const candidatos: any[] = Array.isArray(simResp?.data) ? simResp.data : [];
      if (candidatos.length > 0) {
        // Qué viene marcado de entrada:
        //
        //   1. el producto que el admin eligió en «¿ya lo compraste antes?» —
        //      ya declaró cuál es, volver a preguntárselo sin marcarlo es hacerle
        //      repetir la elección;
        //   2. si no usó el buscador, el que ya está en el destino;
        //   3. si no, nada: elegir por él uno de otra bodega sería adivinar.
        //
        // Ninguna opción viene deshabilitada por estar en otro lugar. Elegirla
        // manda el stock al destino de la compra igual — es el backend el que
        // resuelve si lo suma o si crea la existencia allá.
        setFusionState({
          open: true,
          payload,
          candidatos,
          seleccionado:
            candidatos.find((c) => recompra && c.id === recompra.id)?.id ??
            candidatos.find((c) => c.enDestino && c.accion === "fusion")?.id ??
            null,
        });
        return; // la decisión sigue en el modal de fusión
      }
    }
    await finalizarCreacion(payload);
  };

  // Crea (o fusiona, si payload.fusionarConId viene) y sube las imágenes
  // pendientes al producto resultante. Cierra el modal del formulario.
  const finalizarCreacion = async (payload: any) => {
    const res = await onCreate(payload);
    const newId = res?.data?.id ?? null;
    // Qué pasó de verdad. El toast de `apiFetch` dice siempre "Producto creado
    // correctamente", y en dos de los tres desenlaces eso no es lo que ocurrió:
    // el stock pudo sumarse a una fila que ya existía, o haber nacido como otra
    // existencia de un producto que ya vivía en otra ubicación. Sin decirlo, el
    // admin no tiene cómo saber si quedó un producto o dos.
    if (res?.message && (res.nuevaExistencia || res.fusionado)) {
      getMsg()?.info(res.message);
    }
    // Si la creación/fusión FALLÓ (apiFetch devuelve null: 400 de formato
    // incompatible, categoría inválida, caída de red), NO cerramos ni
    // reseteamos el formulario: el admin perdería todo lo tipeado y las
    // imágenes pegadas. Solo cerramos el prompt de fusión para que pueda
    // corregir y reintentar. apiFetch ya mostró el toast de error.
    if (newId == null) {
      setFusionState({ open: false, payload: null, candidatos: [], seleccionado: null });
      return;
    }
    if (newId && pendingFiles.length > 0) {
      for (const file of pendingFiles) {
        if (file.size > MAX_UPLOAD_BYTES) {
          getMsg()?.error(
            `${file.name}: demasiado grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máx 25 MB.`,
          );
          continue;
        }
        try {
          const toUpload = await compressImageFile(file);
          await uploadMediaFile(newId, toUpload);
        } catch {
          getMsg()?.error(`Error subiendo archivo: ${file.name}`);
        }
      }
    }
    setFusionState({ open: false, payload: null, candidatos: [], seleccionado: null });
    setPendingFiles([]);
    setIsFormModalVisible(false);
    form.resetFields();
    resetIntermediacion();
  };

  /**
   * Resuelve el prompt de fusión. `fusionar=false` crea una fila aparte.
   * El flag `fusionSubmitting` bloquea ambos botones mientras el POST está en
   * vuelo: sin él, un segundo click duplicaba la compra (stock sumado 2×,
   * dos transacciones y, si el destino es propiedad, dos cobros al cliente).
   */
  const submitFusion = async (fusionar: boolean) => {
    if (fusionSubmitting || !fusionState.payload) return;
    setFusionSubmitting(true);
    try {
      await finalizarCreacion(
        fusionar
          ? { ...fusionState.payload, fusionarConId: fusionState.seleccionado }
          : fusionState.payload,
      );
    } finally {
      setFusionSubmitting(false);
    }
  };

  const confirmHideProduct = async () => {
    await form.validateFields();
    const values = form.getFieldsValue();
    await handleHideProduct(values, hideState.id);
    form.resetFields();
    closeHide();
  };

  const esPack = Form.useWatch("esPack", form);

  const onFormValuesChange = (changed: any, all: any) => {
    if ("esPack" in changed && !changed.esPack) {
      form.setFieldsValue({
        unidadesPorPack: undefined,
        cantidadDePacks: undefined,
      });
      return;
    }
    if (all.esPack) {
      const up = all.unidadesPorPack || 0;
      const cp = all.cantidadDePacks || 0;
      form.setFieldsValue({ cantidadDeUnidades: up * cp });
    }

    // EDITANDO no se deriva ningún precio (round 2026-08-21).
    //
    // «Precio total pagado» se siembra al abrir como precio × cantidad, y esta
    // función deriva precio = total / cantidad ante CUALQUIER cambio del
    // formulario. En edición eso convertía una corrección de stock en una
    // reescritura silenciosa del precio: bajar 35 unidades a 30 movía el precio
    // unitario de $958 a $1.118 sin ninguna boleta detrás, en esa existencia y
    // no en sus hermanas.
    //
    // El precio de un producto es el promedio ponderado de todas sus compras y
    // lo calcula el backend; acá no hay nada que derivar. Se corrige editando la
    // transacción en la pestaña Finanzas, que es la que arrastra los montos.
    if (editingProduct) return;

    const precioTotal = all.precioTotal || 0;
    const esPackVal = all.esPack;
    const up = all.unidadesPorPack || 0;
    const cp = all.cantidadDePacks || 0;
    const cu = esPackVal ? up * cp : all.cantidadDeUnidades || 0;

    if (precioTotal > 0) {
      if (esPackVal && cp > 0) {
        form.setFieldsValue({ precioPorPack: Math.ceil(precioTotal / cp) });
      }
      if (cu > 0) {
        form.setFieldsValue({ precioPorUnidad: Math.ceil(precioTotal / cu) });
      }
    } else {
      form.setFieldsValue({
        precioPorPack: undefined,
        precioPorUnidad: undefined,
      });
    }
  };

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="inventario-page"
      contentClass="home-content"
    >
      <div className="inventario-container">
        <Row
          justify="space-between"
          align="middle"
          className="inventario-header"
        >
          <PageHeader
            title="Inventario"
            helpText="Busca y gestiona productos por ubicación, marca y características. Movimientos es la bitácora: todo lo que se compró y se trasladó, con rango de fechas libre y filtro por lugar (muestra tanto lo que entró como lo que salió de él). Finanzas concentra TODA la plata del inventario (compras, cobros a clientes, reembolsos y el desglose mensual por categoría) — no aparece en /flujos."
          />
          <div className="inventario-controls">
            {vista === "productos" && (
              <>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openFormModal("create")}
                >
                  Crear producto
                </Button>
                {/* Agrupación (round 2026-08-06, etapa 2). Las dos preguntas
                    que la tabla plana no sabe responder: "¿cuánto tengo de
                    esto en total y repartido dónde?" y "¿qué hay en esta
                    bodega?". El modo se recuerda entre sesiones. */}
                <Tooltip title="Agrupa la tabla. Por producto: una fila por artículo, se despliega a sus ubicaciones. Por ubicación: una fila por lugar, se despliega a lo que hay ahí.">
                  <Select<ModoAgrupacion>
                    className="inventario-agrupacion"
                    value={agruparPor}
                    onChange={cambiarAgrupacion}
                    options={[
                      { label: "Sin agrupar", value: null },
                      { label: "Por producto", value: "producto" },
                      { label: "Por ubicación", value: "ubicacion" },
                    ]}
                  />
                </Tooltip>
                <Select
                  className="inventario-page-size"
                  value={size}
                  onChange={cambiarSize}
                >
                  <Option value={25}>25</Option>
                  <Option value={50}>50</Option>
                  <Option value={100}>100</Option>
                </Select>
                <Dropdown
                  menu={{ items: columnMenu }}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <Button icon={<SettingOutlined />} />
                </Dropdown>
              </>
            )}
          </div>
        </Row>
        {/* Selector de vista (round 2026-07-26). Finanzas es admin-only a
            nivel backend; la página completa ya es admin-only en App.tsx. */}
        <Segmented
          style={{ marginBottom: 16 }}
          value={vista}
          onChange={(v) => setVista(v as "productos" | "movimientos" | "finanzas")}
          options={[
            { label: "Productos", value: "productos" },
            { label: "Movimientos", value: "movimientos" },
            { label: "Finanzas", value: "finanzas" },
          ]}
        />
        {vista === "finanzas" && <InventarioFinanzasTab />}
        {/* La bitácora reusa las listas de ubicaciones que la página ya cargó
            para sus filtros: volver a pedirlas sería la misma data dos veces. */}
        {vista === "movimientos" && (
          <InventarioMovimientosTab
            propiedades={properties}
            bodegas={bodegas}
            bodeguitas={bodeguitas}
          />
        )}
        {/* La vista Productos se OCULTA con display:none (no se desmonta)
            para conservar filtros, página y selección al alternar. */}
        <div style={{ display: vista === "productos" ? undefined : "none" }}>
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Buscar por nombre"
            allowClear
            size="large"
            prefix={<SearchOutlined />}
            className="inventario-search-input"
            value={filterName}
            onChange={(e) =>
              handleNameSearchChange(e.target.value || undefined)
            }
            onPressEnter={() => void aplicarFiltros()}
          />
        </div>
        {/*
          Round 2026-04-30 nonies — filtros avanzados rediseñados.
          Antes: 7 selects iguales en una grilla plana, sin etiquetas de
          grupo, sin indicación visual de cuántos filtros estaban activos
          ("Filtros avanzados" colapsado a veces ocultaba 5 filtros y el
          usuario no se daba cuenta). Sin chips para quitar uno solo.
          Ahora: secciones tituladas (Geografía, Ubicación), chips
          arriba con los filtros activos + botón individual de X, badge
          numérico en el header del Collapse.
        */}
        {(() => {
          /*
            Round 2026-08-05 — los chips ahora cubren LOS NUEVE filtros.
            Antes el contador decía "Filtros activos (5)" pero el único chip
            que se dibujaba era el de la búsqueda libre: con el panel colapsado
            el admin veía que había filtros puestos y no podía saber cuáles ni
            sacarlos de a uno. Se pasó de <span> con estilos a mano a <Tag
            closable> de Ant Design, que ya trae la × accesible y sigue el tema.
          */
          const nombreDe = (
            lista: any[],
            campo: string,
            id: number | string,
          ): string => {
            const found = lista.find((x: any) => String(x.id) === String(id));
            return (found?.[campo] as string) ?? `#${id}`;
          };

          type Chip = {
            key: string;
            etiqueta: string;
            texto: string;
            /** El campo de `FiltrosInventario` al que pertenece, si viaja ahí. */
            campo?: keyof FiltrosInventario;
            valor?: number | string;
            color: string;
            /** Cómo se quita, para los que no son parte de `FiltrosInventario`. */
            quitar?: () => void;
          };
          const chips: Chip[] = [];

          if (filterName?.trim()) {
            chips.push({
              key: "nombre",
              etiqueta: "Nombre",
              texto: filterName.trim(),
              campo: "filterName",
              color: "blue",
            });
          }
          if (advancedFilter?.trim()) {
            chips.push({
              key: "busqueda",
              etiqueta: "Búsqueda",
              texto: advancedFilter.trim(),
              campo: "advancedFilter",
              color: "orange",
            });
          }
          filterCategorias.forEach((c) =>
            chips.push({
              key: `cat-${c}`,
              etiqueta: "Categoría",
              texto:
                c === SIN_CATEGORIA ? "Sin categoría" : (CATEGORIA_LABEL[c] ?? c),
              campo: "filterCategorias",
              valor: c,
              color: "cyan",
            }),
          );
          if (filterStock) {
            chips.push({
              key: "stock",
              etiqueta: "Stock",
              texto: filterStock === "con" ? "Con stock" : "Sin stock",
              campo: "filterStock",
              color: "green",
            });
          }
          /*
            "Mostrar ocultos" NO es parte de `FiltrosInventario` a propósito: si
            lo fuera, `hayFiltros()` daría true y la página se iría al endpoint
            pesado de filtro sin que haya ningún filtro puesto. Pero sí cambia
            lo que trae la tabla —suma los productos dados de baja a las filas,
            al total del paginador y a las sumas de cada cabecera de grupo— y,
            desde que se recuerda entre sesiones (round 2026-09-24), quedaba
            encendido sin ninguna señal: el panel arranca colapsado y el badge
            marcaba 0. Va como chip para que se vea y se pueda apagar de a uno.
          */
          if (incluirOcultos) {
            chips.push({
              key: "ocultos",
              etiqueta: "Mostrando",
              texto: "productos dados de baja",
              color: "default",
              quitar: () => {
                setIncluirOcultos(false);
                // Vuelta a la página 1, igual que cualquier otro chip (los
                // demás la hacen dentro de `quitarFiltro`): apagarlo achica la
                // lista y desde la página 3 se queda en una que ya no existe.
                setPage(1);
              },
            });
          }
          const geo: Array<
            [keyof FiltrosInventario, (number | string)[], any[], string, string]
          > = [
            ["filterCountries", filterCountries, countries, "pais", "País"],
            ["filterRegions", filterRegions, regions, "region", "Región"],
            ["filterComunas", filterComunas, comunas, "comuna", "Comuna"],
            ["filterSectors", filterSectors, sectors, "sector", "Sector"],
          ];
          geo.forEach(([campo, valores, lista, prop, etiqueta]) =>
            valores.forEach((id) =>
              chips.push({
                key: `${campo}-${id}`,
                etiqueta,
                texto: nombreDe(lista, prop, id),
                campo,
                valor: id,
                color: "geekblue",
              }),
            ),
          );
          const ubic: Array<
            [keyof FiltrosInventario, (number | string)[], any[], string, boolean]
          > = [
            ["filterProperties", filterProperties, properties, "Propiedad", true],
            ["filterBodegas", filterBodegas, bodegas, "Bodega", false],
            ["filterBodeguitas", filterBodeguitas, bodeguitas, "Bodeguita", false],
          ];
          ubic.forEach(([campo, valores, lista, etiqueta, esPropiedad]) =>
            valores.forEach((id) => {
              const bruto = nombreDe(lista, "nombre", id);
              chips.push({
                key: `${campo}-${id}`,
                etiqueta,
                // Nombre de propiedad SIEMPRE en mayúsculas al mostrarlo
                // (regla del proyecto, utils/propertyDisplay).
                texto: esPropiedad ? formatPropertyName(bruto) : bruto,
                campo,
                valor: id,
                color: "purple",
              });
            }),
          );

          const activeCount = chips.length;

          /*
            Cascada geográfica. Antes los cuatro selects listaban SIEMPRE el
            catálogo completo: elegir "Chile" no reducía las regiones, y para
            filtrar por una comuna había que saberse de memoria a qué región
            pertenecía. Ahora cada nivel acota al siguiente, y la geografía
            elegida acota además las propiedades/bodegas/bodeguitas ofrecidas.

            Las listas del hook traen la geografía como NOMBRE (no id), así
            que primero se traducen los ids seleccionados a nombres.
          */
          const nombresSel = (
            lista: any[],
            campo: string,
            ids: (number | string)[],
          ) => new Set(ids.map((id) => nombreDe(lista, campo, id)));

          const paisesSel = nombresSel(countries, "pais", filterCountries);
          const regionesSel = nombresSel(regions, "region", filterRegions);
          const comunasSel = nombresSel(comunas, "comuna", filterComunas);
          const sectoresSel = nombresSel(sectors, "sector", filterSectors);

          /** Un nivel sin selección no acota nada. */
          const enSet = (set: Set<string>, valor: any) =>
            set.size === 0 || set.has(valor);

          /**
           * Acota una lista, pero NUNCA esconde un valor ya seleccionado: si
           * lo hiciera, el chip seguiría existiendo y el select mostraría el
           * id crudo en vez del nombre.
           */
          const acotar = <T extends { id: number | string }>(
            lista: T[],
            seleccion: (number | string)[],
            pasa: (x: any) => boolean,
          ): T[] =>
            lista.filter(
              (x) =>
                pasa(x) || seleccion.some((s) => String(s) === String(x.id)),
            );

          const regionesVisibles = acotar(regions, filterRegions, (r) =>
            enSet(paisesSel, r.pais),
          );
          const comunasVisibles = acotar(
            comunas,
            filterComunas,
            (c) => enSet(paisesSel, c.pais) && enSet(regionesSel, c.region),
          );
          const sectoresVisibles = acotar(
            sectors,
            filterSectors,
            (s) =>
              enSet(paisesSel, s.pais) &&
              enSet(regionesSel, s.region) &&
              enSet(comunasSel, s.comuna),
          );

          const geoOk = (x: any) =>
            enSet(paisesSel, x.pais) &&
            enSet(regionesSel, x.region) &&
            enSet(comunasSel, x.comuna) &&
            enSet(sectoresSel, x.sector);

          const propiedadesVisibles = acotar(properties, filterProperties, geoOk);
          const bodegasVisibles = acotar(bodegas, filterBodegas, geoOk);
          const bodeguitasVisibles = acotar(bodeguitas, filterBodeguitas, geoOk);

          return (
            <>
              {activeCount > 0 && (
                <div
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    alignItems: "center",
                  }}
                >
                  <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                    Filtros activos ({activeCount}):
                  </Typography.Text>
                  {chips.map((c) => (
                    <Tag
                      key={c.key}
                      color={c.color}
                      closable
                      onClose={(e) => {
                        e.preventDefault();
                        if (c.quitar) {
                          c.quitar();
                          return;
                        }
                        if (c.campo) void quitarFiltro(c.campo, c.valor);
                      }}
                      style={{ marginInlineEnd: 0 }}
                    >
                      {c.etiqueta}: {c.texto}
                    </Tag>
                  ))}
                  <Button
                    type="link"
                    size="small"
                    danger
                    onClick={() => void limpiarFiltros()}
                    style={{ paddingLeft: 4 }}
                  >
                    Limpiar todo
                  </Button>
                </div>
              )}
              <Collapse
                ghost
                className="inventario-filtros"
                style={{ marginBottom: 16 }}
                items={[
                  {
                    key: "filtros-avanzados",
                    label: (
                      <span style={{ fontWeight: 500 }}>
                        <FilterOutlined style={{ marginRight: 8 }} />
                        Filtros avanzados
                        {activeCount > 0 && (
                          <Badge
                            count={activeCount}
                            style={{ marginLeft: 8 }}
                            title={`${activeCount} filtro${activeCount === 1 ? "" : "s"} aplicado${activeCount === 1 ? "" : "s"}`}
                          />
                        )}
                      </span>
                    ),
                    children: (
                      <>
                        <div style={{ marginBottom: 16 }}>
                          <label
                            className="inventario-filtros-label"
                            htmlFor="inventario-busqueda-libre"
                          >
                            Búsqueda libre
                          </label>
                          <Input
                            id="inventario-busqueda-libre"
                            // Solo VALORES (el backend exige que cada palabra
                            // matchee alguna característica): "rx7600 rojo"
                            // busca modelo rx7600 Y color rojo. Escribir el
                            // nombre del campo ("color rojo") no funciona —
                            // el placeholder anterior lo sugería (aud. 2026-07-13).
                            placeholder="Ej: rx7600 rojo (solo valores, sin nombre de campo)"
                            allowClear
                            value={advancedFilter}
                            onChange={(e) => {
                              setAdvancedFilter(e.target.value);
                              programarAplicar();
                            }}
                            onPressEnter={() => void aplicarFiltros()}
                          />
                        </div>

                        {/* Round 2026-08-05 — grupo "Producto". La tabla ya
                            mostraba la categoría y el stock, pero no se podía
                            filtrar por ninguno de los dos: para encontrar lo
                            agotado había que recorrer las páginas a ojo. */}
                        <div style={{ marginBottom: 12 }}>
                          <div className="inventario-filtros-grupo">Producto</div>
                          <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12} md={12}>
                              <Select
                                mode="multiple"
                                showSearch
                                placeholder="Categoría"
                                optionFilterProp="label"
                                allowClear
                                value={filterCategorias}
                                onChange={(v) => {
                                  setFilterCategorias(v);
                                  programarAplicar();
                                }}
                                style={{ width: "100%" }}
                                options={[
                                  ...FLUJO_CATEGORIAS.map((c) => ({
                                    value: c,
                                    label: CATEGORIA_LABEL[c] ?? c,
                                  })),
                                  { value: SIN_CATEGORIA, label: "Sin categoría" },
                                ]}
                              />
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Select
                                placeholder="Stock"
                                allowClear
                                value={filterStock}
                                onChange={(v) => {
                                  setFilterStock(v);
                                  programarAplicar();
                                }}
                                style={{ width: "100%" }}
                                options={[
                                  { value: "con", label: "Con stock" },
                                  { value: "sin", label: "Sin stock (agotado)" },
                                ]}
                              />
                            </Col>
                            {/* Los productos dados de baja no salían en
                                ninguna vista, y con ellos desaparecía su
                                botón de eliminar: ocultar era un camino de
                                ida. Round 2026-08-06. */}
                            <Col xs={24} sm={12} md={4}>
                              <Tooltip title="Muestra también los productos dados de baja (ocultos). Desde ahí puedes devolverlos a la lista o eliminarlos.">
                                <Space size={6}>
                                  <Switch
                                    size="small"
                                    checked={incluirOcultos}
                                    onChange={(v) => {
                                      setIncluirOcultos(v);
                                      // Cambia cuántas filas hay: la página en
                                      // la que estaba el admin ya no significa
                                      // lo mismo (y al apagarlo puede no
                                      // existir).
                                      setPage(1);
                                    }}
                                  />
                                  <span style={{ fontSize: 13 }}>Mostrar ocultos</span>
                                </Space>
                              </Tooltip>
                            </Col>
                          </Row>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                          <div className="inventario-filtros-grupo">
                            Geografía
                          </div>
                          <Row gutter={[12, 12]}>
                            <Col xs={12} sm={12} md={6}>
                              <Select
                                mode="multiple"
                                showSearch
                                placeholder="País"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  fuzzyIncludes(option?.children, input)
                                }
                                allowClear
                                onChange={(v) => { setFilterCountries(v); programarAplicar(); }}
                                value={filterCountries}
                                style={{ width: "100%" }}
                              >
                                {countries.map((c) => (
                                  <Option key={c.id} value={c.id}>
                                    {c.pais}
                                  </Option>
                                ))}
                              </Select>
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                              <Select
                                mode="multiple"
                                showSearch
                                placeholder="Región"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  fuzzyIncludes(option?.children, input)
                                }
                                allowClear
                                onChange={(v) => { setFilterRegions(v); programarAplicar(); }}
                                value={filterRegions}
                                style={{ width: "100%" }}
                              >
                                {regionesVisibles.map((r) => (
                                  <Option key={r.id} value={r.id}>
                                    {r.region}
                                  </Option>
                                ))}
                              </Select>
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                              <Select
                                mode="multiple"
                                showSearch
                                placeholder="Comuna"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  fuzzyIncludes(option?.children, input)
                                }
                                allowClear
                                onChange={(v) => { setFilterComunas(v); programarAplicar(); }}
                                value={filterComunas}
                                style={{ width: "100%" }}
                              >
                                {comunasVisibles.map((c) => (
                                  <Option key={c.id} value={c.id}>
                                    {c.comuna}
                                  </Option>
                                ))}
                              </Select>
                            </Col>
                            <Col xs={12} sm={12} md={6}>
                              <Select
                                mode="multiple"
                                showSearch
                                placeholder="Sector"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  fuzzyIncludes(option?.children, input)
                                }
                                allowClear
                                onChange={(v) => { setFilterSectors(v); programarAplicar(); }}
                                value={filterSectors}
                                style={{ width: "100%" }}
                              >
                                {sectoresVisibles.map((s) => (
                                  <Option key={s.id} value={s.id}>
                                    {s.sector}
                                  </Option>
                                ))}
                              </Select>
                            </Col>
                          </Row>
                        </div>

                        <div style={{ marginBottom: 12 }}>
                          <div className="inventario-filtros-grupo">
                            Ubicación
                          </div>
                          <Row gutter={[12, 12]}>
                            <Col xs={24} sm={12} md={8}>
                              <Select
                                mode="multiple"
                                showSearch
                                placeholder="Propiedad"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  fuzzyIncludes(option?.children, input)
                                }
                                allowClear
                                onChange={(v) => { setFilterProperties(v); programarAplicar(); }}
                                value={filterProperties}
                                style={{ width: "100%" }}
                              >
                                {propiedadesVisibles.map((p) => (
                                  <Option key={p.id} value={p.id}>
                                    {formatPropertyName(p.nombre)}
                                  </Option>
                                ))}
                              </Select>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Select
                                mode="multiple"
                                showSearch
                                placeholder="Bodega"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  fuzzyIncludes(option?.children, input)
                                }
                                allowClear
                                onChange={(v) => { setFilterBodegas(v); programarAplicar(); }}
                                value={filterBodegas}
                                style={{ width: "100%" }}
                              >
                                {bodegasVisibles.map((b) => (
                                  <Option key={b.id} value={b.id}>
                                    {b.nombre}
                                  </Option>
                                ))}
                              </Select>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                              <Select
                                mode="multiple"
                                showSearch
                                placeholder="Bodeguita"
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  fuzzyIncludes(option?.children, input)
                                }
                                allowClear
                                onChange={(v) => { setFilterBodeguitas(v); programarAplicar(); }}
                                value={filterBodeguitas}
                                style={{ width: "100%" }}
                              >
                                {bodeguitasVisibles.map((bb) => (
                                  <Option key={bb.id} value={bb.id}>
                                    {bb.nombre}
                                  </Option>
                                ))}
                              </Select>
                            </Col>
                          </Row>
                        </div>

                        {/* Ya no hay botón "Aplicar filtros": cada cambio se
                            aplica solo (con rebote). Se deja el de limpiar,
                            que sí es una acción explícita, y una nota para que
                            no se busque el botón que estaba antes. */}
                        <Space style={{ marginTop: 8 }} align="center">
                          <Button
                            onClick={() => void limpiarFiltros()}
                            disabled={!hayQueLimpiar}
                          >
                            Limpiar filtros
                          </Button>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Los filtros se aplican solos al elegirlos.
                          </Typography.Text>
                        </Space>
                      </>
                    ),
                  },
                ]}
              />
            </>
          );
        })()}
        <Modal
          open={isFormModalVisible}
          title={editingProduct ? "Editar producto" : "Crear producto"}
          onCancel={() => setIsFormModalVisible(false)}
          onOk={() => form.submit()}
          destroyOnClose
          // Modal más ancho para que el layout horizontal en 3 columnas
          // respire (sm/md=8 en cantidad/precio, características, etc.).
          width={960}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinishForm}
            onValuesChange={onFormValuesChange}
          >
            {/*
              Selector arriba del modal — clarifica si el usuario está
              ingresando una compra nueva (entrada de stock externa con
              boleta/factura) o un movimiento (transfer entre ubicaciones
              internas). Cambia el label de la fecha y, al elegir
              "Movimiento", precarga origenTipo con "bodegas" en lugar de
              "otros" (compra). En modo edición se oculta porque el dato
              ya existe y este toggle no debería sobrescribirlo.
            */}
            {!editingProduct && (
              <Row gutter={16} style={{ marginBottom: 8 }}>
                <Col span={24}>
                  <Segmented
                    block
                    options={[
                      { label: "Compra nueva (boleta/factura)", value: "compra" },
                      { label: "Movimiento de stock interno", value: "movimiento" },
                    ]}
                    value={tipoOperacion}
                    onChange={(v) => {
                      const next = v as "compra" | "movimiento";
                      setTipoOperacion(next);
                      // Default razonable de origen según la operación.
                      // No pisamos si el usuario ya escogió uno.
                      const currentOrigen = form.getFieldValue("origenTipo");
                      if (!currentOrigen) {
                        const defaultOrigen =
                          next === "compra" ? "otros" : "bodegas";
                        form.setFieldsValue({ origenTipo: defaultOrigen });
                        setOrigenType(defaultOrigen);
                      }
                    }}
                  />
                </Col>
              </Row>
            )}

            {/* Round 2026-08-06 — «¿Ya lo compraste antes?». Re-tipear la ficha
                de memoria es el mecanismo por el que nacieron los duplicados:
                cualquier diferencia mínima creaba un producto distinto. Solo en
                creación: al editar, la ficha ya está cargada. */}
            {!editingProduct && (
              <RecomprarBuscador
                elegido={recompra}
                onElegir={aplicarRecompra}
                onLimpiar={limpiarRecompra}
              />
            )}

            <Row gutter={16}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Nombre"
                  name="nombre"
                  rules={[{ required: true, message: "Ingresa un nombre" }]}
                >
                  {/* El nombre es donde más duele un duplicado: es lo que
                      identifica al producto. Ver CampoEtiqueta. */}
                  <CampoEtiqueta
                    opciones={suggestions.nombre}
                    placeholder="Ej: Sábana, Toalla..."
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6} md={6}>
                <Form.Item
                  label="Categoría"
                  name="categoria"
                  tooltip="Categoría financiera del producto — la misma clasificación que usan los flujos de dinero (/flujos). Es un atributo PERMANENTE, como el color o la marca: acompaña al producto en compras, movimientos, bajas y reportes mensuales (pestaña Finanzas), sin importar qué se haga con él. Es opcional: un producto puede quedar sin categoría. Si la corriges después, se actualiza también en todas sus transacciones históricas."
                >
                  <Select
                    allowClear
                    placeholder="Sin categoría"
                    options={FLUJO_CATEGORIAS.map((c) => ({
                      value: c,
                      label: CATEGORIA_LABEL[c] ?? c,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6} md={6}>
                {/*
                  Round 2026-08-05 — este campo NO es un dato del producto: es
                  la fecha del hecho financiero y vive en
                  `inventario_transacciones.fecha`. En edición se hidrata desde
                  el backend (GET /inventario/:id/intermediario) y, si el admin
                  la cambia, `updateItem` corrige la transacción de compra y
                  mueve con ella sus flujos vinculados. Antes el campo salía
                  vacío y lo tipeado se descartaba en silencio.
                */}
                <Form.Item
                  label={
                    tipoOperacion === "movimiento"
                      ? "Fecha del movimiento"
                      : "Fecha de compra"
                  }
                  name="fecha"
                  tooltip={
                    editingProduct
                      ? tieneCompraRegistrada
                        ? "Fecha real de la compra. Corregirla mueve el egreso —y el cobro al cliente o el reembolso asociados— al mes que indiques. Los montos NO cambian: eso se corrige en la pestaña Finanzas."
                        : "Este producto no tiene una compra registrada (nació de un movimiento), así que no hay fecha de compra que corregir."
                      : "Día en que se hizo la compra. Es la fecha con la que el egreso entra a Finanzas, no la de hoy."
                  }
                >
                  <DatePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    disabled={!!editingProduct && !tieneCompraRegistrada}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  name="esPack"
                  label="Es pack"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              {esPack && (
                <>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="Unidades por pack" name="unidadesPorPack">
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="Cantidad de packs" name="cantidadDePacks">
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Cantidad de unidades"
                  name="cantidadDeUnidades"
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    readOnly={!!esPack}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item
                  label="Precio total pagado"
                  name="precioTotal"
                  extra={
                    editingProduct ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        El precio no se edita acá: es el promedio ponderado de todas
                        las compras del producto. Para corregir cuánto costó, edita
                        la transacción en la pestaña Finanzas.
                      </Typography.Text>
                    ) : undefined
                  }
                >
                  <InputNumber
                    min={0}
                    style={{ width: "100%" }}
                    readOnly={!!editingProduct}
                    formatter={clpInputFormatter}
                    parser={clpInputParser}
                  />
                </Form.Item>
              </Col>
              {esPack && (
                <Col xs={24} sm={12} md={8}>
                  <Form.Item label="Precio pack" name="precioPorPack">
                    <InputNumber
                      readOnly
                      style={{ width: "100%" }}
                      formatter={clpInputFormatter} parser={clpInputParser}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={12} md={8}>
                <Form.Item label="Precio unidad" name="precioPorUnidad">
                  <InputNumber readOnly style={{ width: "100%" }} formatter={clpInputFormatter} parser={clpInputParser} />
                </Form.Item>
              </Col>
            </Row>

            {/*
              Round 2026-04-30 nonies — antes había un Select "Agregar
              campos" que el usuario tenía que abrir y elegir uno por uno
              cada campo opcional (marca, modelo, color, etc.). UX horrible
              porque te obligaba a recordar qué existía. Ahora todos los
              campos opcionales se renderizan siempre, agrupados en
              secciones colapsables. Lo "opcional" sigue siéndolo en la
              data (campos vacíos no se persisten), pero el formulario te
              los muestra a la mano.
            */}
            <Collapse
              ghost
              defaultActiveKey={["caracteristicas"]}
              style={{ marginBottom: 16 }}
              items={[
                {
                  key: "caracteristicas",
                  label: (
                    <span style={{ fontWeight: 500 }}>
                      Características del producto
                    </span>
                  ),
                  children: (
                    <>
                      <Row gutter={16}>
                        {[
                          { key: "marca", label: "Marca" },
                          { key: "modelo", label: "Modelo" },
                          { key: "sku", label: "SKU" },
                          { key: "material", label: "Material" },
                          { key: "color", label: "Color" },
                          { key: "forma", label: "Forma" },
                          { key: "diseno", label: "Diseño" },
                          { key: "tamano", label: "Tamaño" },
                        ].map(({ key, label }) => (
                          <Col xs={24} sm={12} md={6} key={key}>
                            <Form.Item label={label} name={key}>
                              {/* Round 2026-08-06 — el campo avisa cuando lo
                                  escrito se parece a un valor que ya existe
                                  («vlanco» ⇒ ¿Blanco?). Ver CampoEtiqueta. */}
                              <CampoEtiqueta opciones={suggestions[key]} />
                            </Form.Item>
                          </Col>
                        ))}
                      </Row>

                      <Row gutter={16}>
                        <Col xs={24} sm={12} md={12}>
                          <Form.Item label="Volumen">
                            <Space.Compact style={{ width: "100%" }}>
                              <Form.Item name="volumenValor" noStyle>
                                <InputNumber
                                  placeholder="10"
                                  style={{ width: "70%" }}
                                  min={0}
                                />
                              </Form.Item>
                              <Form.Item name="volumenUnidad" noStyle>
                                <Select
                                  placeholder="Unidad"
                                  style={{ width: "30%" }}
                                  options={[
                                    { label: "L", value: "L" },
                                    { label: "ml", value: "ml" },
                                    { label: "m\u00B3", value: "m\u00B3" },
                                    { label: "unidad", value: "unidad" },
                                  ]}
                                />
                              </Form.Item>
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={12}>
                          <Form.Item label="Peso">
                            <Space.Compact style={{ width: "100%" }}>
                              <Form.Item name="pesoValor" noStyle>
                                <InputNumber
                                  placeholder="10"
                                  style={{ width: "70%" }}
                                  min={0}
                                />
                              </Form.Item>
                              <Form.Item name="pesoUnidad" noStyle>
                                <Select
                                  placeholder="Unidad"
                                  style={{ width: "30%" }}
                                  options={[
                                    { label: "kg", value: "kg" },
                                    { label: "g", value: "g" },
                                    { label: "L", value: "L" },
                                    { label: "ml", value: "ml" },
                                    { label: "m\u00B3", value: "m\u00B3" },
                                    { label: "unidad", value: "unidad" },
                                  ]}
                                />
                              </Form.Item>
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ),
                },
                {
                  key: "documentos",
                  label: (
                    <span style={{ fontWeight: 500 }}>
                      Documentos (opcional)
                    </span>
                  ),
                  children: (
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={12}>
                        <Form.Item label="N° Boleta" name="numeroBoleta">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={12}>
                        <Form.Item label="N° Factura" name="numeroFactura">
                          <Input />
                        </Form.Item>
                      </Col>
                    </Row>
                  ),
                },
              ]}
            />

            <Row gutter={16}>
              {/*
                Round 2026-08-05 — el ORIGEN también vive en la transacción, no
                en el Inventory. En edición se hidrata (antes el Select quedaba
                en blanco) y va DESHABILITADO: `updateItem` no lo persiste, y un
                campo editable cuyo valor se descarta en silencio es
                exactamente el bug que se corrigió en la fecha. Cambiar el
                origen de una compra ya registrada = eliminar y recrear
                (docs/modulo-inventario.md §3.1).
              */}
              <Col xs={24} sm={12} md={12}>
                <Form.Item
                  label="Origen de compra"
                  name="origenTipo"
                  tooltip={
                    editingProduct
                      ? "De dónde vino esta compra. Es parte del registro histórico y no se edita: para cambiarlo hay que eliminar el producto y recrearlo."
                      : undefined
                  }
                >
                  <Select
                    placeholder="Selecciona origen"
                    onChange={setOrigenType}
                    allowClear
                    disabled={!!editingProduct}
                  >
                    <Option value="otros">Tienda / Proveedor</Option>
                    <Option value="bodegas">Bodega</Option>
                    <Option value="bodeguitas">Bodeguita</Option>
                    <Option value="propiedades">Propiedad</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                {origenType === "otros" && (
                  <Form.Item label="Nombre del origen" name="origenNombre">
                    <AutoComplete
                      options={(suggestions.origen_nombre ?? []).map((v) => ({
                        value: v,
                      }))}
                      filterOption={(input, option) =>
                        fuzzyIncludes(option?.value, input)
                      }
                      placeholder="Ej: Easy, Líder, MercadoLibre"
                      // Sin aviso de parecido acá: el campo se deshabilita al
                      // editar y en la creación el proveedor se elige casi
                      // siempre del desplegable, así que el aviso sería ruido.
                      disabled={!!editingProduct}
                    />
                  </Form.Item>
                )}
                {origenType === "bodegas" && (
                  <Form.Item label="Bodega origen" name="origenId">
                    <Select showSearch filterOption={(input, option) => fuzzyIncludes(option?.children, input)} placeholder="Selecciona bodega" disabled={!!editingProduct}>
                      {bodegas.map((b) => (
                        <Option key={b.id} value={b.id}>
                          {b.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                {origenType === "bodeguitas" && (
                  <Form.Item label="Bodeguita origen" name="origenId">
                    <Select showSearch filterOption={(input, option) => fuzzyIncludes(option?.children, input)} placeholder="Selecciona bodeguita" disabled={!!editingProduct}>
                      {bodeguitas.map((bb) => (
                        <Option key={bb.id} value={bb.id}>
                          {bb.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                {origenType === "propiedades" && (
                  <Form.Item label="Propiedad origen" name="origenId">
                    <Select showSearch filterOption={(input, option) => fuzzyIncludes(option?.children, input)} placeholder="Selecciona propiedad" disabled={!!editingProduct}>
                      {properties.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {formatPropertyName(p.nombre)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12} md={12}>
                <Form.Item label="Destino (ubicación)" name="ubicacionTipo">
                  <Select
                    placeholder="Selecciona tipo"
                    onChange={setLocationType}
                  >
                    <Option value="bodegas">Bodega</Option>
                    <Option value="bodeguitas">Bodeguita</Option>
                    <Option value="propiedades">Propiedad</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                {locationType === "bodegas" && (
                  <Form.Item label="Bodega" name="ubicacionId">
                    <Select showSearch filterOption={(input, option) => fuzzyIncludes(option?.children, input)} placeholder="Selecciona bodega">
                      {bodegas.map((b) => (
                        <Option key={b.id} value={b.id}>
                          {b.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                {locationType === "bodeguitas" && (
                  <Form.Item label="Bodeguita" name="ubicacionId">
                    <Select showSearch filterOption={(input, option) => fuzzyIncludes(option?.children, input)} placeholder="Selecciona bodeguita">
                      {bodeguitas.map((bb) => (
                        <Option key={bb.id} value={bb.id}>
                          {bb.nombre}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                {locationType === "propiedades" && (
                  <Form.Item label="Propiedad" name="ubicacionId">
                    <Select showSearch filterOption={(input, option) => fuzzyIncludes(option?.children, input)} placeholder="Selecciona propiedad">
                      {properties.map((p) => (
                        <Option key={p.id} value={p.id}>
                          {formatPropertyName(p.nombre)}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
            </Row>

            {/*
              Round 2026-06-19 — transparencia financiera (mandato del owner:
              cada actor debe ver sus movimientos). Al crear una COMPRA con
              destino propiedad, el backend genera la RECUPERACIÓN (cobro al
              cliente, ingreso de AF). Lo avisamos acá para que el admin sepa
              que esto le cobra al cliente, y que es ajustable/regalo después.
            */}
            {!editingProduct &&
              tipoOperacion === "compra" &&
              locationType === "propiedades" && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Esto se le cobrará al cliente de la propiedad"
                  description="Entregar el producto a una propiedad genera la recuperación: se le cobra al cliente el costo (queda como ingreso de Aloja Fácil) y aparece en su pestaña Productos. Podrás ajustar el monto o marcarlo como regalo ($0) después, desde la pestaña Finanzas de este módulo. Para eliminar el cobro hay que eliminar el producto (reversa toda la cascada)."
                />
              )}

            {/* ───────────────────────────────────────────────────────
                Estado de los flujos derivados (decisión #8, 2026-06-21)
                ───────────────────────────────────────────────────────
                Solo en EDICIÓN de un producto que vino de una compra
                (`origenType` hidratado) y está hoy entregado a una propiedad.
                Avisa si falta la RECUPERACIÓN (cobro al cliente) y ofrece
                regenerarla — útil si se borró por error desde /flujos. La
                transacción de inventario es la fuente de verdad durable, así
                que el cobro se rehace con un upsert idempotente (no duplica). */}
            {editingProduct &&
              editingProduct.ubicacionTipo === "propiedades" &&
              !!origenType &&
              (tieneRecuperacion ? (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginTop: 12, marginBottom: 12 }}
                  message="Cobro al cliente registrado"
                  description="Entregar este producto a la propiedad generó la recuperación (ingreso de Aloja Fácil). Su monto se ajusta, o se marca como regalo ($0), desde la pestaña Finanzas de este módulo."
                />
              ) : (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 12, marginBottom: 12 }}
                  message="Falta el cobro al cliente de este producto"
                  description={
                    <>
                      Este producto está en una propiedad pero no tiene la
                      recuperación (cobro al cliente) asociada — probablemente se
                      borró por error. Regenerarla recrea el cobro al monto
                      HISTÓRICO de la compra (no al precio actual del producto:
                      editar el producto nunca toca el pasado). Después puedes
                      ajustarlo o dejarlo en $0 como regalo desde la pestaña
                      Finanzas.
                      {origenType === "otros" && !tieneReembolso && (
                        <>
                          {" "}
                          El reembolso a un tercero, si alguien adelantó el
                          dinero, se rehace marcando el intermediario más abajo.
                        </>
                      )}
                    </>
                  }
                  action={
                    <Button
                      size="small"
                      type="primary"
                      loading={regenerandoFlujos}
                      onClick={handleRegenerarFlujos}
                    >
                      Regenerar cobro
                    </Button>
                  }
                />
              ))}

            {/* ───────────────────────────────────────────────────────
                Sección "Intermediación" (round 2026-06-01)
                ───────────────────────────────────────────────────────
                Aparece tanto en CREACIÓN como en EDICIÓN, condicionada
                a tab Compra nueva + origen externo ("otros"). Permite
                registrar que el dinero de la boleta lo adelantó un humano
                (aseador / trabajador / etc.) y que AF debe reembolsarlo.

                En MODO EDICIÓN (iter b, round 2026-06-01):
                  - Se hidrata el estado desde GET /api/inventario/:id/intermediario
                    al abrir el modal.
                  - Si admin cambia algo (toggle, rol, usuario), se setea el
                    flag `intermediarioModificado=true` y al guardar el
                    backend regenera los flujos derivados (delete + create
                    desde la matriz canónica).
                  - Si admin no toca esta sección, NO se envía el bloque y
                    los flujos derivados existentes quedan intactos (solo
                    se recalcula el monto si cambió precio/cantidad).

                Backend (services/inventoryFlowGenerator.js) materializa
                el reembolso como:
                  - destino propiedad → 2 rows paired (intermediario):
                    cliente paga a AF + AF paga al humano.
                  - destino bodega → 1 row (gasto AF): AF paga al humano.

                Si admin no marca el toggle, AF se asume como pagador
                directo y solo se crea el cobro al cliente (destino
                propiedad) o nada (destino bodega).
            */}
            {tipoOperacion === "compra" && origenType === "otros" && (
              <div
                style={{
                  marginTop: 12,
                  marginBottom: 12,
                  padding: 12,
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                }}
              >
                <Row gutter={16} align="middle">
                  <Col flex="auto">
                    <Typography.Text strong>¿Alguien adelantó el dinero por AF?</Typography.Text>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                      {editingProduct ? (
                        <>
                          Si modificas este bloque, los flujos de reembolso vinculados a esta compra se regenerarán (delete + create). El cobro al cliente y el reembolso quedarán alineados al nuevo intermediario.
                        </>
                      ) : (
                        <>
                          Marca esta opción si la boleta de {form.getFieldValue("origenNombre") || "el proveedor"} la pagó otra persona con su tarjeta o efectivo. Se creará un reembolso pendiente AF→persona vinculado a esta compra.
                        </>
                      )}
                    </div>
                  </Col>
                  <Col>
                    <Switch
                      checked={hayIntermediario}
                      onChange={(checked) => {
                        setHayIntermediario(checked);
                        setIntermediarioModificado(true);
                        if (!checked) {
                          setIntermediarioRol("");
                          setIntermediarioUsuarioId(undefined);
                          setIntermediarioRecipientId(undefined);
                        }
                      }}
                    />
                  </Col>
                </Row>
                {hayIntermediario && (
                  <Row gutter={16} style={{ marginTop: 12 }}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="¿Quién adelantó el dinero?" required>
                        <Select
                          placeholder="Selecciona rol"
                          value={intermediarioRol || undefined}
                          onChange={(v) => {
                            setIntermediarioRol(v);
                            setIntermediarioUsuarioId(undefined);
                            setIntermediarioRecipientId(undefined);
                            setIntermediarioModificado(true);
                          }}
                        >
                          <Option value="aseador">Aseador</Option>
                          <Option value="trabajador">Trabajador</Option>
                          <Option value="propietario">Propietario / Cliente</Option>
                          <Option value="arrendatario">Arrendatario</Option>
                          <Option value="receptor_externo">Receptor externo (catálogo)</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      {(intermediarioRol === "aseador" ||
                        intermediarioRol === "trabajador" ||
                        intermediarioRol === "propietario") && (
                        <Form.Item label="Usuario específico" required>
                          <Select
                            showSearch
                            filterOption={(input, option) =>
                              fuzzyIncludes(option?.label, input)
                            }
                            placeholder={`Selecciona ${intermediarioRol}`}
                            value={intermediarioUsuarioId}
                            onChange={(v) => {
                              setIntermediarioUsuarioId(v);
                              setIntermediarioModificado(true);
                            }}
                            options={usuariosFinanzas
                              .filter((u) => {
                                const r = String(u.rol || "").toLowerCase();
                                if (intermediarioRol === "aseador") return r === "aseador";
                                if (intermediarioRol === "trabajador") return r === "trabajador";
                                // Propietario en DB vive como rol "cliente"
                                if (intermediarioRol === "propietario") return r === "cliente";
                                return false;
                              })
                              .map((u) => userSelectOption(u))}
                          />
                        </Form.Item>
                      )}
                      {intermediarioRol === "receptor_externo" && (
                        <Form.Item label="Receptor externo" required>
                          <Select
                            showSearch
                            filterOption={(input, option) =>
                              fuzzyIncludes(option?.label, input)
                            }
                            placeholder="Selecciona receptor"
                            value={intermediarioRecipientId}
                            onChange={(v) => {
                              setIntermediarioRecipientId(v);
                              setIntermediarioModificado(true);
                            }}
                            options={expenseRecipients.map((r) => ({ value: r.id, label: r.nombre }))}
                          />
                        </Form.Item>
                      )}
                      {intermediarioRol === "arrendatario" && (
                        <Alert
                          type="info"
                          showIcon
                          style={{ marginTop: 24 }}
                          message="Arrendatario es una entidad abstracta — no requiere usuario_id."
                        />
                      )}
                    </Col>
                  </Row>
                )}
              </div>
            )}

            {!editingProduct && (
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Imágenes (opcional)">
                    <Upload.Dragger
                      multiple
                      accept="image/*,video/*"
                      fileList={pendingFiles.map((f, i) => ({
                        uid: `${i}`,
                        name: f.name,
                        status: "done" as const,
                        thumbUrl: URL.createObjectURL(f),
                      }))}
                      beforeUpload={(file) => {
                        setPendingFiles((prev) => [...prev, file]);
                        return false;
                      }}
                      onRemove={(file) => {
                        setPendingFiles((prev) =>
                          prev.filter((_, i) => `${i}` !== file.uid),
                        );
                      }}
                      listType="picture"
                    >
                      <p style={{ fontSize: 28, color: "#999", margin: 0 }}>
                        <InboxOutlined />
                      </p>
                      <p style={{ margin: "4px 0 0", color: "#666" }}>
                        Arrastra imágenes o haz click para seleccionar
                      </p>
                    </Upload.Dragger>
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Form>
        </Modal>
        <Modal
          open={isImageModalVisible}
          title="Imágenes"
          onCancel={() => {
            setIsImageModalVisible(false);
            setImages([]);
            setImagesLoading(false);
          }}
          footer={null}
          width={960}
          // Deja un poco de espacio para que no tape los dots
          styles={{
            body: { paddingBottom: 56, maxHeight: "80vh", overflow: "hidden" },
          }}
        >
          {/*
            Orden de ramas importante: spinner primero, luego carrusel, luego
            placeholder vacío. Invertir mostraría "sin imágenes" durante la
            carga y luego saltaría de tamaño al renderizar el carrusel.
          */}
          {imagesLoading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "60vh",
              }}
            >
              <Spin size="large" tip="Cargando imágenes..." />
            </div>
          ) : images.length > 0 ? (
            <Carousel
              arrows // ← muestra flechas
              dots // ← muestra indicadores
              infinite={images.length > 1}
              afterChange={(idx) => setCurrentSlide(idx)}
              // si notas “saltos”, usa una altura fija del contenedor del slide
            >
              {images.map((url, i) => {
                const isVideo = /\.(mp4|mov|webm|avi|mkv)(\?|$)/i.test(url);
                return (
                <div key={url}>
                  <div
                    style={{
                      position: "relative",
                      height: "60vh",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#111",
                    }}
                  >
                    {isVideo ? (
                      <VideoPlayer
                        src={url}
                        previewSrc={videoLivianas[url] ?? null}
                        ajuste="llenar"
                      />
                    ) : (
                    <div
                      onClick={() => {
                        setLbIndex(i);
                        setLbOpen(true);
                      }}
                      style={{
                        cursor: "zoom-in",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={url}
                        alt={`Imagen ${i + 1}`}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                    )}

                    {/* Botón eliminar (overlay) */}
                    <Popconfirm
                      title="Eliminar imagen"
                      description="Esta acción eliminará la imagen definitivamente. ¿Deseas continuar?"
                      okText="Eliminar"
                      cancelText="Cancelar"
                      okButtonProps={{
                        danger: true,
                        loading: deletingUrl === url,
                      }}
                      onConfirm={async () => {
                        setDeletingUrl(url);
                        try {
                          await handleDeleteImage(url); // ← tu handler que llama al API
                          getMsg()?.success("Imagen eliminada correctamente");
                        } catch (e: any) {
                          getMsg()?.error(
                            e?.message || "No se pudo eliminar la imagen",
                          );
                        } finally {
                          setDeletingUrl(null);
                        }
                      }}
                    >
                      <Button
                        danger
                        size="small"
                        disabled={!!deletingUrl && deletingUrl !== url}
                        style={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          zIndex: 10,
                        }}
                      >
                        {deletingUrl === url ? "Eliminando..." : "Eliminar"}
                      </Button>
                    </Popconfirm>

                    {/* Indicador x / n */}
                    <div
                      style={{
                        position: "absolute",
                        left: 12,
                        top: 12,
                        zIndex: 9,
                        background: "rgba(0,0,0,.55)",
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                    >
                      {currentSlide + 1} / {images.length}
                    </div>
                  </div>
                </div>
                );
              })}
            </Carousel>
          ) : (
            <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
              No hay imágenes para este producto.
            </div>
          )}
          <Upload.Dragger
            accept="image/*,video/*"
            showUploadList={false}
            disabled={saving}
            beforeUpload={(file) => {
              uploadDirect(file);
              return false;
            }}
            style={{ marginTop: 16 }}
          >
            <p style={{ fontSize: 28, color: "#999", margin: 0 }}>
              <InboxOutlined />
            </p>
            <p style={{ margin: "4px 0 0", color: "#666" }}>
              Arrastra una imagen o video (máx 25 MB)
            </p>
          </Upload.Dragger>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Button
              icon={<CameraOutlined />}
              onClick={() => {
                const input = document.createElement("input");
                input.type = "file";
                input.accept = "image/*";
                input.capture = "environment";
                input.onchange = (e) => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (!file) return;
                  uploadDirect(file);
                };
                input.click();
              }}
              style={{ flex: 1 }}
            >
              Tomar foto
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => setIsEditorVisible(true)}
              disabled={saving}
              style={{ flex: 1 }}
            >
              Editar imagen
            </Button>
          </div>
          {(() => {
            const imageOnly = images.filter((u) => !/\.(mp4|mov|webm|avi|mkv)(\?|$)/i.test(u));
            const adjustedIndex = Math.min(lbIndex, Math.max(imageOnly.length - 1, 0));
            return (
              <Lightbox
                open={lbOpen}
                close={() => setLbOpen(false)}
                index={adjustedIndex}
                slides={imageOnly.map((u) => ({ src: u }))}
                plugins={[Zoom]}
                zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
                carousel={{ finite: imageOnly.length <= 1 }}
              />
            );
          })()}
        </Modal>

        <ImageEditorModal
          open={isEditorVisible}
          onClose={() => setIsEditorVisible(false)}
          onSave={handleEditorSave}
          saving={saving}
        />

        {/*
          Modal de movimiento extraído a MoveProductModal — round
          2026-04-27 (Fase 8). El padre mantiene el state porque el
          useEffect de buscar similares depende de moveParams.
        */}
        <MoveProductModal
          open={isMoveModalVisible}
          onCancel={() => setIsMoveModalVisible(false)}
          onConfirm={handleConfirmMove}
          moveParams={moveParams}
          setMoveParams={setMoveParams}
          similarProducts={similarProducts}
          selectedSimilarId={selectedSimilarId}
          setSelectedSimilarId={setSelectedSimilarId}
          bodegas={bodegas}
          bodeguitas={bodeguitas}
          properties={properties}
          inventario={inventario}
        />

        {/* Línea de tiempo del producto (2026-08-06). Se abre desde el botón
            de la fila y desde la pestaña Finanzas. */}
        <HistoriaProductoDrawer
          inventarioId={historia?.id ?? null}
          nombreFallback={historia?.nombre ?? null}
          open={historia != null}
          onClose={() => setHistoria(null)}
        />

        {/* Doble confirmación de borrado con impacto explícito (2026-07-26). */}
        <DeleteProductModal
          open={deleteState.open}
          productos={deleteState.productos}
          deleting={deletingId !== null || bulkDeleting}
          onCancel={() => setDeleteState({ open: false, productos: [], origen: "fila" })}
          onConfirm={confirmarEliminacion}
        />

        {/* «Estos son el mismo producto» (2026-08-06): une las FICHAS, no el
            stock. Cada lugar conserva sus unidades y su precio. */}
        <UnificarProductosModal
          open={unificarAbierto}
          productos={inventario.filter((p) => idsSeleccionados.includes(p.id))}
          idsFueraDeVista={idsSeleccionados.filter(
            (id) => !inventario.some((p) => p.id === id),
          )}
          confirmando={unificando}
          onCancel={() => setUnificarAbierto(false)}
          onConfirm={confirmarUnificacion}
        />

        {/* «¿Alguno de estos es el mismo producto?» (2026-07-26, generalizado
            el 2026-08-20). Nació como fusión —solo servía contra una fila que ya
            estuviera en el destino— y ahora acepta cualquier candidato: el stock
            va al destino de la compra en los dos casos, sumándose a la fila que
            ya está ahí o naciendo como otra existencia de la misma ficha. */}
        <Modal
          open={fusionState.open}
          title="¿Alguno de estos es el mismo producto?"
          onCancel={
            fusionSubmitting
              ? undefined
              : () =>
                  setFusionState({
                    open: false,
                    payload: null,
                    candidatos: [],
                    seleccionado: null,
                  })
          }
          maskClosable={!fusionSubmitting}
          footer={[
            <Button
              key="nuevo"
              disabled={fusionSubmitting}
              onClick={() => void submitFusion(false)}
            >
              No, es otro producto
            </Button>,
            <Button
              key="fusionar"
              type="primary"
              loading={fusionSubmitting}
              // Lo único que lo bloquea es no haber elegido nada. Estar en otra
              // ubicación dejó de ser un impedimento el 2026-08-20: era la regla
              // que obligaba a comprar en la bodega equivocada para después
              // mover el stock a mano.
              disabled={fusionState.seleccionado == null}
              onClick={() => void submitFusion(true)}
            >
              Sí, es el mismo producto
            </Button>,
          ]}
          width={620}
        >
          {/* Round 2026-08-20 — acá vivía la prohibición. Un candidato en otra
              ubicación salía deshabilitado con el cartel «lo correcto es
              comprarlo ahí y después moverlo», y la única salida era crear una
              ficha nueva: un duplicado, justo lo que el catálogo vino a evitar.
              Ahora el destino de la compra manda siempre y lo que se muestra es
              qué va a pasar con cada opción. */}
          {fusionState.candidatos.some((c: any) => !c.enDestino) && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Hay coincidencias en otras ubicaciones"
              description="Marcadas en violeta más abajo. Puedes elegirlas igual: el stock de esta compra va al destino que elegiste, no a donde está esa fila. Si el producto todavía no existe ahí, se crea su existencia en el destino y queda hermanada con las demás — una sola ficha y un solo precio promedio, sin tener que comprar en otra bodega para después mover el stock a mano."
            />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="¿Qué pasa si digo que es el mismo?"
            description="El stock SIEMPRE aterriza en el destino que elegiste en el formulario. Si ahí ya hay una fila de ese producto con el mismo formato, se le suma; si no, nace una existencia nueva en el destino colgada de la misma ficha. En los dos casos el precio del producto se recalcula como el promedio PONDERADO por cantidad de TODAS sus compras, las de todos los lugares juntas (compraste 100 a $10 y 200 a $15 ⇒ 300 a $13,34): es un solo producto, así que tiene un solo precio. La compra queda registrada igual con SU fecha y SU precio real en la pestaña Finanzas — el historial financiero no se promedia, y los cobros a clientes salen de ahí, no del promedio. Elige «No, es otro producto» solo si de verdad es otra cosa (otra marca, otro artículo): eso crea una ficha aparte."
          />
          <Radio.Group
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
            value={fusionState.seleccionado}
            onChange={(e) =>
              setFusionState((s) => ({ ...s, seleccionado: e.target.value }))
            }
          >
            {fusionState.candidatos.map((c: any) => {
              const motivo = queHaraElCandidato(c);
              return (
                <Radio key={c.id} value={c.id} disabled={fusionSubmitting}>
                  <Tag color="default" className="inventario-id-tag">
                    #{c.id}
                  </Tag>{" "}
                  <strong>{c.nombre}</strong>
                  {c.marca ? ` — ${c.marca}` : ""}{" "}
                  {c.categoria ? (
                    <Tag color="geekblue">{CATEGORIA_LABEL[c.categoria] ?? c.categoria}</Tag>
                  ) : null}
                  {c.oculto ? <Tag color="orange">dado de baja</Tag> : null}
                  {!c.enDestino && c.ubicacionNombre ? (
                    <Tag color="purple">{formatPropertyName(c.ubicacionNombre)}</Tag>
                  ) : null}
                  <span style={{ color: "#6b7280", fontSize: 12 }}>
                    {" "}
                    ({c.es_pack
                      ? `${c.cantidad_de_packs} pack(s) de ${c.unidades_por_pack} — $${Number(c.precio_por_pack || 0).toLocaleString("es-CL")} c/u`
                      : `${c.cantidad_de_unidades} unidad(es) — $${Number(c.precio_por_unidad || 0).toLocaleString("es-CL")} c/u`}
                    {motivo})
                  </span>
                </Radio>
              );
            })}
          </Radio.Group>
        </Modal>

        <Modal
          open={hideState.open}
          title={`Ocultar producto${
            hideState.nombre ? `: ${hideState.nombre}` : ""
          }`}
          okText="Ocultar"
          cancelText="Cancelar"
          onOk={confirmHideProduct}
          onCancel={closeHide}
          okButtonProps={{ loading, disabled: hideState.id == null }}
          destroyOnClose
          maskClosable={!loading}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Ocultar no elimina registros financieros"
            description="El producto dejará de aparecer en el inventario, pero sus compras y movimientos se mantienen en finanzas. Si necesitas borrar también el impacto financiero, usa Eliminar en su lugar."
          />
          <Form
            form={form}
            layout="vertical"
            initialValues={{ razon: undefined, detalle: "" }}
            disabled={loading}
          >
            <Form.Item
              name="razon"
              label="Razón"
              rules={[{ required: true, message: "Selecciona una razón" }]}
            >
              <Select
                placeholder="Selecciona una razón"
                options={REASONS}
                showSearch
                filterOption={(input, option) =>
                  fuzzyIncludes(option?.label, input)
                }
              />
            </Form.Item>

            <Form.Item
              name="detalle"
              label="Detalle"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const r = getFieldValue("razon");
                    if (r === "otro" && (!value || !value.trim())) {
                      return Promise.reject(
                        new Error("Describe el motivo al elegir 'Otro'"),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              extra={
                <Typography.Text type="secondary">
                  {form.getFieldValue("razon") === "otro"
                    ? "Requerido al elegir 'Otro'."
                    : "Opcional. Puedes anotar una breve descripción."}
                </Typography.Text>
              }
            >
              <TextArea
                placeholder={
                  form.getFieldValue("razon") === "otro"
                    ? "Describe el motivo..."
                    : "Detalle (opcional)"
                }
                autoSize={{ minRows: 3, maxRows: 6 }}
                showCount
                maxLength={500}
              />
            </Form.Item>
          </Form>
        </Modal>
        {error && <div className="inventario-error">{error}</div>}
        {loading || !ubicacionesReady ? (
          <Spin tip="Cargando inventario..." />
        ) : (
          <>
            {/*
              Barra contextual de borrado masivo. Solo visible para
              admin/superadmin con al menos un producto seleccionado en la
              tabla. El borrado masivo, al igual que el individual, es
              destructivo: elimina cada producto y TODO su registro
              financiero asociado (compras, movimientos). No es ocultar.
            */}
            {isAdmin && idsSeleccionados.length > 0 && (
              <div
                style={{
                  marginBottom: 12,
                  padding: "8px 12px",
                  // Neutral desde el round 2026-08-06: la barra dejó de ser
                  // solo "borrar" y pintarla de rojo hacía ver toda acción de
                  // selección como destructiva. El peligro lo señala el botón.
                  background: "var(--color-surface-secondary, #fafafa)",
                  border: "1px solid var(--border-color, #d9d9d9)",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                {/* `preserveSelectedRowKeys` mantiene la selección al paginar
                    y al cambiar filtros — necesario para borrar en masa a lo
                    largo de varias páginas, pero deja seleccionados productos
                    que ya no se ven. Se dice explícitamente cuántos son: antes
                    la barra decía "5 seleccionados" mostrando 2 en pantalla y
                    el borrado se llevaba los otros 3 sin avisar. */}
                {(() => {
                  const visibles = new Set(inventario.map((p) => p.id));
                  const fuera = idsSeleccionados.filter(
                    (id) => !visibles.has(id),
                  ).length;
                  return (
                    <span>
                      <strong>{idsSeleccionados.length}</strong> producto
                      {idsSeleccionados.length === 1 ? "" : "s"} seleccionado
                      {idsSeleccionados.length === 1 ? "" : "s"}
                      {fuera > 0 && (
                        <Typography.Text type="warning" style={{ marginLeft: 8 }}>
                          ({fuera} fuera de la vista actual)
                        </Typography.Text>
                      )}
                    </span>
                  );
                })()}
                <Space>
                  <Button onClick={() => setSelectedRowKeys([])} disabled={bulkDeleting}>
                    Limpiar selección
                  </Button>
                  {/* Round 2026-08-06 — la herramienta para los duplicados que
                      YA existen. Une las FICHAS, no el stock. */}
                  {idsSeleccionados.length > 1 && (
                    <Button
                      icon={<MergeCellsOutlined />}
                      onClick={() => setUnificarAbierto(true)}
                      disabled={bulkDeleting}
                    >
                      Es el mismo producto
                    </Button>
                  )}
                  {/* Round 2026-07-26 — doble confirmación con impacto
                      explícito (reemplaza el Popconfirm de un click). */}
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    loading={bulkDeleting}
                    onClick={() => {
                      const seleccionados = idsSeleccionados
                        .map(
                          (id) =>
                            inventario.find((p) => p.id === id) ??
                            ({ id, nombre: `Producto #${id}` } as Producto),
                        );
                      setDeleteState({
                        open: true,
                        productos: seleccionados,
                        origen: "masivo",
                      });
                    }}
                  >
                    Eliminar seleccionados
                  </Button>
                </Space>
              </div>
            )}
            <Table
              // La clave ya no puede ser `"id"`: las cabeceras de grupo son
              // filas sintéticas sin id propio, y todas colisionarían en 0.
              // Las existencias conservan su id numérico, que es lo que
              // `selectedRowKeys` y todas las acciones siguen usando.
              className="inventario-tabla"
              rowKey={(r: FilaTabla) => r.filaKey}
              columns={columns}
              dataSource={filasTabla}
              childrenColumnName="children"
              // Agrupado: las cabeceras arrancan CERRADAS. Abiertas de entrada
              // la vista sería igual de larga que la plana y la agrupación no
              // serviría de nada.
              expandable={agruparPor ? { defaultExpandAllRows: false } : undefined}
              rowClassName={(r: FilaTabla) => (r.esGrupo ? "inventario-fila-grupo" : "")}
              scroll={{ x: "max-content" }}
              rowSelection={
                isAdmin
                  ? {
                      // Fija junto a la identidad: si la casilla se va con el
                      // scroll, marcar filas para borrar en masa obliga a ir y
                      // volver por cada una.
                      fixed: true as const,
                      selectedRowKeys,
                      // Marcar una cabecera marca sus hijas (AntD cascadea en
                      // datos de árbol). La clave de la cabecera entra igual a
                      // `selectedRowKeys` para que el checkbox se vea marcado,
                      // pero NINGUNA acción la usa: todas filtran a numéricas.
                      // Ver `idsSeleccionados`.
                      onChange: (keys) => setSelectedRowKeys(keys),
                      preserveSelectedRowKeys: true,
                    }
                  : undefined
              }
              // Sin esto la tabla no daba NINGUNA señal mientras volaba el
              // filtro: quedaba mostrando el resultado anterior y parecía que
              // el botón no había hecho nada.
              loading={loading}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      hayFiltrosPuestos
                        ? "Ningún producto coincide con los filtros aplicados."
                        : "Todavía no hay productos en el inventario."
                    }
                  >
                    {hayFiltrosPuestos && (
                      <Button onClick={() => void limpiarFiltros()}>
                        Limpiar filtros
                      </Button>
                    )}
                  </Empty>
                ),
              }}
              pagination={{
                current: page,
                pageSize: size,
                total,
                onChange: setPage,
                // El tamaño de página se cambia con el selector del header,
                // que además vuelve a la página 1.
                showSizeChanger: false,
                // Agrupado, una página contiene GRUPOS y `total` los cuenta.
                // El texto tiene que decir lo que cuenta: un paginador que dice
                // "productos" mientras cuenta ubicaciones (o al revés) es de
                // los errores que nadie detecta porque los números cuadran
                // consigo mismos.
                showTotal: (t, [desde, hasta]) => {
                  const que =
                    agruparPor === "producto"
                      ? `producto${t === 1 ? "" : "s"}`
                      : agruparPor === "ubicacion"
                        ? `ubicación${t === 1 ? "" : "es"}`
                        : `fila${t === 1 ? "" : "s"}`;
                  return `${desde}–${hasta} de ${t} ${que}`;
                },
              }}
              // Round 2026-08-06 — el orden lo resuelve el SERVIDOR. La tabla
              // solo informa qué columna se tocó: con paginación server-side,
              // ordenar en el navegador ordenaría las filas visibles y no el
              // inventario, y el resultado se vería perfectamente correcto.
              //
              // El `extra.action` NO es opcional. AntD dispara este mismo
              // `onChange` para ordenar, filtrar Y paginar. Sin distinguir la
              // acción, cada clic en un número de página entraba acá, llamaba a
              // `cambiarOrden` y este volvía a la página 1: el clic en «2»
              // ejecutaba `setPage(2)` e inmediatamente `setPage(1)`, React
              // agrupaba las dos y ganaba la última. La tabla quedaba atrapada
              // en la primera página y no había forma de ver el resto del
              // inventario. Corregido en la segunda revisión del round.
              onChange={(_paginacion, _filtros, sorter, extra) => {
                if (extra?.action !== "sort") return;
                const s = Array.isArray(sorter) ? sorter[0] : sorter;
                cambiarOrden(
                  (s?.columnKey as string) ?? null,
                  (s?.order as "ascend" | "descend" | null) ?? null,
                );
              }}
            />
          </>
        )}
        </div>
      </div>
    </Layout>
  );
};

export default Inventario;
